import { useState, useEffect, useRef, useMemo } from "react";
import api, { formatApiError } from "../lib/api";
import WhatsAppLink from "./WhatsAppLink";
import MultiCellulare from "./MultiCellulare";

const MultiCellulareOpt = ({ on, children, ...p }) => (on ? <MultiCellulare {...p}>{children}</MultiCellulare> : children);
import { fetchTipologie, asPairs, DEFAULT_TIPOLOGIE_SINISTRO, tipoFlags } from "../lib/tipologieSinistro";
import { guardCreate } from "../context/DuplicateGuard";
import SimilarContacts from "./SimilarContacts";
import { withCfSesso } from "../lib/cfSesso";
import { guardTarga } from "../context/TargaGuard";
import { formatDate } from "../lib/format";
import { isValidTarga } from "../lib/targa";
import { formatNominativo } from "../lib/nominativo";
import { displayName, ANAG_TYPES, TITOLI } from "../lib/anagConfig";
import { computeBareme as computeBarem } from "../lib/bareme";
import DateInput from "./DateInput";
import AddressAutocomplete from "./AddressAutocomplete";
import SegnalatoreSchedaDialog from "./SegnalatoreSchedaDialog";
import ControparteVeicolo from "./ControparteVeicolo";
import VeicoliEditor from "./VeicoliEditor";
import { searchComuni } from "../lib/comuni";
import { REQUIREABLE_FIELDS, DEFAULT_REQUIRED } from "../lib/requiredFields";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Checkbox } from "./ui/checkbox";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Calendar as CalendarIcon, MapPin, Plus, Trash2, ChevronRight, ChevronLeft, ChevronUp, ChevronDown, Loader2, Search, CheckCircle2, Check, ChevronsUpDown, Sparkles, X, Pencil, AlertTriangle, Car } from "lucide-react";
import { toast } from "sonner";
import { CodiceFiscale } from "codice-fiscale-js";
import DecodificaCFButton from "./DecodificaCFButton";
import { useNonAderenti, NON_ADERENTE_MSG } from "../lib/convenzione";

const PROCEDURA_CARD = ["CARD", "DOPPIA FIRMA", "NO CARD", "NON APPLICABILE"];
// Visibilità veicolo per tipo pratica: configurata in Impostazioni → Tipologie Sinistro.
let TIPI_LIST = [];
const veicoloVisibile = (tipo) => tipoFlags(TIPI_LIST, tipo).veicolo;
const LESIONI = ["NESSUNA", "LIEVI", "GRAVI", "GRAVISSIME", "DECESSO"];
const VEICOLO_TIPI = [
  { v: "", l: "SELEZIONARE VEICOLO -" },
  { v: "veicolo non a motore", l: "veicolo non a motore" },
  { v: "autoveicolo", l: "autoveicolo" },
  { v: "ciclomotore/motoveicolo/macchina operatrice", l: "ciclomotore/motoveicolo/macchina operatrice" },
  { v: "ciclomotore/motoveicolo - 'targhino'", l: "ciclomotore/motoveicolo - 'targhino'" },
  { v: "macchina agricola/rimorchio/operatrice non targata", l: "macchina agricola/rimorchio/operatrice non targata" },
];
const SESSO = ["Maschio", "Femmina"];
const DOCUMENTI = ["Carta identità", "Passaporto", "Patente", "Altro"];
const RUOLI = ["Proprietario Conducente", "Proprietario", "Conducente", "Terzo Trasportato", "Pedone", "Ciclista"];
// Visibilità della sezione "Conducente del veicolo" in base al Ruolo Sinistro:
//  - Proprietario Conducente / Conducente -> conducente coincide col soggetto -> sezione nascosta
//  - Pedone / Ciclista (ruoli senza veicolo) -> sezione nascosta (nessun veicolo)
//  - altri ruoli con veicolo (Proprietario, Terzo Trasportato, ...) -> sezione visibile
const RUOLI_CONDUCENTE_COINCIDE = ["Proprietario Conducente", "Conducente"];
const RUOLI_SENZA_VEICOLO = ["Pedone", "Ciclista"];
// Un ruolo "richiede veicolo" se è selezionato e non è un ruolo senza veicolo (Pedone/Ciclista).
const ruoloRichiedeVeicolo = (ruolo) => {
  const r = String(ruolo || "").trim();
  return !!r && !RUOLI_SENZA_VEICOLO.includes(r);
};
const conducenteSectionVisibile = (ruolo) => {
  const r = String(ruolo || "").trim();
  if (!r) return false;
  if (RUOLI_CONDUCENTE_COINCIDE.includes(r)) return false;
  if (RUOLI_SENZA_VEICOLO.includes(r)) return false;
  return true;
};
const AUTORITA_TIPI = ["Carabinieri", "Polizia di Stato", "Polizia Municipale", "Guardia di Finanza", "Polizia Stradale", "Vigili del Fuoco", "altro"];
const BAREM_CASI = [
  { v: "", l: "-- seleziona caso --" },
  { v: "0 -", l: "0 -" },
  { v: "1 - in sosta/in fermata", l: "1 - in sosta/in fermata" },
  { v: "2 - ripartiva da una sosta/apriva una portiera", l: "2 - ripartiva da una sosta/apriva una portiera" },
  { v: "3 - stava parcheggiando", l: "3 - stava parcheggiando" },
  { v: "4 - usciva da un parcheggio/da un luogo privato/da una strada vicinale", l: "4 - usciva da un parcheggio/da un luogo privato/da una strada vicinale" },
  { v: "5 - entrava in un parcheggio/in un luogo privato/in una strada vicinale", l: "5 - entrava in un parcheggio/in un luogo privato/in una strada vicinale" },
  { v: "6 - si immetteva in una piazza a senso rotatorio", l: "6 - si immetteva in una piazza a senso rotatorio" },
  { v: "7 - circolava in una piazza a senso rotatorio", l: "7 - circolava in una piazza a senso rotatorio" },
  { v: "8 - tamponava procedendo nello stesso senso e nella stessa fila", l: "8 - tamponava procedendo nello stesso senso e nella stessa fila" },
  { v: "9 - procedeva nello stesso senso ma in una fila diversa", l: "9 - procedeva nello stesso senso ma in una fila diversa" },
  { v: "10 - cambiava fila", l: "10 - cambiava fila" },
  { v: "11 - sorpassava", l: "11 - sorpassava" },
  { v: "12 - girava a destra", l: "12 - girava a destra" },
  { v: "13 - girava a sinistra", l: "13 - girava a sinistra" },
  { v: "14 - retrocedeva", l: "14 - retrocedeva" },
  { v: "15 - invadeva la sede stradale riservata alla circolazione in senso inverso", l: "15 - invadeva la sede stradale riservata alla circolazione in senso inverso" },
  { v: "16 - proveniva da destra", l: "16 - proveniva da destra" },
  { v: "17 - non osservava il segnale di precedenza o di semaforo rosso", l: "17 - non osservava il segnale di precedenza o di semaforo rosso" },
];

// Calcolo responsabilità secondo la tabella di barème ufficiale (matrice in lib/bareme.js).
const STEPS = ["TIPO PRATICA", "DATI SINISTRO", "DATI CLIENTI", "DATI CONTROPARTE", "DATI TESTIMONE", "CONFERMA"];

// Validazione formato targa italiana (non bloccante). Vuoto = nessun avviso.

const toISO = (d) => {
  if (!d) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const todayISO = () => toISO(new Date());
const INPUT_CLS = "mt-1.5 h-9 bg-slate-900/60 border-slate-700 text-slate-100";
const LBL = "text-[11px] uppercase tracking-wider text-slate-600 font-mono";

// Calcolo CF dai dati già presenti (formati anagrafica: aaaa-mm-gg, gg/mm/aaaa, M/F, "COMUNE (PR)").
function buildCF(o) {
  const ds = String(o.data_nascita || "").trim();
  let y, m, d;
  if (/^\d{4}-\d{1,2}-\d{1,2}/.test(ds)) [y, m, d] = ds.slice(0, 10).split("-").map(Number);
  else if (/^\d{1,2}[/.-]\d{1,2}[/.-]\d{4}$/.test(ds)) [d, m, y] = ds.split(/[/.-]/).map(Number);
  const sx = String(o.sesso || "").trim();
  const comune = String(o.comune_nascita || "").replace(/\s*\([^)]*\)\s*$/, "").trim();
  const prov = String(o.provincia_nascita || "").trim() || (String(o.comune_nascita || "").match(/\(([A-Za-z]{2})\)\s*$/) || [])[1];
  const missing = [!String(o.cognome || "").trim() && "cognome", !String(o.nome || "").trim() && "nome", !/^[mf]/i.test(sx) && "sesso", !(y && m && d) && "data di nascita", !comune && "comune di nascita"].filter(Boolean);
  if (missing.length) return { error: `Per generare il CF compila: ${missing.join(", ")}` };
  try {
    return { cf: new CodiceFiscale({ name: o.nome.trim(), surname: o.cognome.trim(), gender: /^f/i.test(sx) ? "F" : "M", day: d, month: m, year: y, birthplace: comune, birthplaceProvincia: prov ? prov.toUpperCase() : undefined }).toString() };
  } catch { return { error: `Comune di nascita "${comune}" non riconosciuto: selezionalo dall'elenco per generare il CF` }; }
}

function Combobox({ label, value, onChange, options = [], fetchAtype, loader, preload, allowFree, onSelectOption, testId, w2, placeholder }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(null);
  const [fetched, setFetched] = useState([]);
  const [loaded, setLoaded] = useState([]);
  const [savingNew, setSavingNew] = useState(false);
  const ref = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!fetchAtype || !open || fetched.length) return;
    api.get(`/anagrafiche/${fetchAtype}`).then(({ data }) => setFetched((data || []).map((r) => displayName(r)).filter(Boolean))).catch(() => {});
  }, [fetchAtype, open, fetched.length]);

  useEffect(() => {
    if (!loader || !open) return;
    if (preload) { if (!loaded.length) loader("").then((r) => setLoaded(r || [])).catch(() => {}); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { loader(query || "").then((r) => setLoaded(r || [])).catch(() => {}); }, 250);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, preload]);

  const base = loader ? loaded : (fetchAtype ? fetched : options);
  const opts = base.map((o) => (Array.isArray(o) ? { v: String(o[0]), l: `${o[0]} | ${o[1]}` } : (o && o.v !== undefined ? { ...o, v: String(o.v), l: String(o.l) } : { v: String(o), l: String(o) })));
  const current = opts.find((o) => o.v === value);
  const text = query != null ? query : (current ? current.l : (allowFree ? (value || "") : ""));
  const filtered = query ? opts.filter((o) => o.l.toLowerCase().includes(query.toLowerCase())) : opts;
  const qt = (query || "").trim();
  const canAdd = !!fetchAtype && !!qt && !opts.some((o) => o.l.toLowerCase() === qt.toLowerCase() || o.v.toLowerCase() === qt.toLowerCase());

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(null); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const choose = (o) => { onChange(o.v); if (onSelectOption) onSelectOption(o); setQuery(null); setOpen(false); };

  const addNew = async () => {
    if (!qt || !fetchAtype) return;
    const cfg = ANAG_TYPES.find((t) => t.key === fetchAtype);
    const azienda = cfg && cfg.fields.some((f) => f.key === "denominazione");
    setSavingNew(true);
    try {
      const { action, record } = await guardCreate(fetchAtype, azienda ? { denominazione: qt } : { nome: qt });
      const dn = displayName(record);
      setFetched((prev) => (prev.includes(dn) ? prev : [...prev, dn]));
      onChange(dn); setQuery(null); setOpen(false);
      if (action === "created") toast.success("Aggiunto in anagrafica");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingNew(false); }
  };

  return (
    <div className={w2 ? "sm:col-span-2" : ""} ref={ref}>
      {label && <Label className={LBL}>{label}</Label>}
      <div className="relative mt-1.5">
        <Input value={text} placeholder={placeholder || "Digita o seleziona…"}
          onChange={(e) => { const t = e.target.value; setQuery(t); setOpen(true); if (allowFree) onChange(t); }}
          onFocus={() => setOpen(true)}
          data-testid={testId} className={`h-9 ${text ? "pr-14" : "pr-8"} bg-slate-900/60 border-slate-700 text-slate-100`} />
        {text ? (
          <button type="button" tabIndex={-1} onClick={() => { onChange(""); setQuery(null); if (onSelectOption) onSelectOption({ v: "", prov: "", cap: "" }); }}
            title="Cancella" aria-label="Cancella campo" data-testid={testId ? `${testId}-clear` : undefined}
            className="absolute right-8 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:text-white hover:bg-slate-600/70 transition-colors">
            <X className="h-3.5 w-3.5" />
          </button>
        ) : null}
        <ChevronsUpDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 opacity-60 pointer-events-none text-slate-400" />
        {open && (
          <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-700 bg-[#121824] shadow-xl max-h-60 overflow-y-auto" data-testid={`${testId}-list`}>
            {filtered.map((o) => (
              <button key={o.v} type="button" onMouseDown={(e) => { e.preventDefault(); choose(o); }} data-testid={`${testId}-opt`}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800/60 flex items-center gap-2 ${value === o.v ? "text-amber-400" : "text-slate-200"}`}>
                <Check className={`h-4 w-4 shrink-0 ${value === o.v ? "opacity-100" : "opacity-0"}`} /> {o.l}
              </button>
            ))}
            {filtered.length === 0 && !canAdd && <div className="px-3 py-2 text-sm text-slate-500">Nessun risultato</div>}
            {canAdd && (
              <button type="button" onMouseDown={(e) => { e.preventDefault(); addNew(); }} disabled={savingNew} data-testid={`${testId}-add-new`}
                className="w-full text-left px-3 py-2 text-sm bg-blue-600/20 text-blue-300 hover:bg-blue-600/30 border-t border-slate-700 flex items-center gap-2 font-medium">
                {savingNew ? <Loader2 className="h-4 w-4 animate-spin shrink-0" /> : <Plus className="h-4 w-4 shrink-0" />} Aggiungi "{qt}"
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DateField({ label, value, onChange, testId, w2 }) {
  return (
    <div className={w2 ? "sm:col-span-2" : ""}>
      {label && <Label className={LBL}>{label}</Label>}
      <div className="mt-1.5"><DateInput value={value} onChange={onChange} testId={testId} className="h-9 bg-slate-900/60 border-slate-700 text-slate-100" /></div>
    </div>
  );
}

function TF({ label, value, onChange, testId, mono, transform, w2, placeholder, wa, extra, onExtra, labels, onLabels }) {
  const has = !!(value != null && String(value).length);
  const inputCls = INPUT_CLS.replace("mt-1.5", "").trim();
  return (
    <div className={w2 ? "sm:col-span-2" : ""}>
      <Label className={`${LBL} ${wa ? "inline-flex items-center gap-1.5" : ""}`}>{label}{wa ? <WhatsAppLink number={value} size="h-3.5 w-3.5" testId={testId ? `${testId}-wa` : undefined} /> : null}</Label>
      <div className="mt-1.5"><MultiCellulareOpt on={!!onExtra} extra={extra} onExtra={onExtra} labels={labels} onLabels={onLabels} testId={testId}><div className="relative">
        <Input value={value || ""} onChange={(e) => onChange(transform ? transform(e.target.value) : e.target.value)} placeholder={placeholder}
          data-testid={testId} className={`${inputCls} ${mono ? "font-mono" : ""} ${has ? "pr-8" : ""}`} />
        {has && (
          <button type="button" tabIndex={-1} onClick={() => onChange("")} title="Cancella" aria-label="Cancella campo"
            data-testid={testId ? `${testId}-clear` : undefined}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:text-white hover:bg-slate-600/70 transition-colors">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div></MultiCellulareOpt></div>
    </div>
  );
}

function MiniRich({ htmlRef, testId }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && htmlRef.current != null && htmlRef.current !== ref.current.innerHTML) ref.current.innerHTML = htmlRef.current; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const cmd = (c) => { document.execCommand(c, false); ref.current?.focus(); };
  const btns = [["bold", "B", "font-bold"], ["italic", "I", "italic"], ["underline", "U", "underline"], ["insertUnorderedList", "•", ""], ["insertOrderedList", "1.", ""]];
  return (
    <div className="mt-1.5 rounded-md border border-slate-700 bg-slate-900/60 overflow-hidden">
      <div className="flex gap-1 border-b border-slate-700 px-2 py-1.5 bg-slate-800/60">
        {btns.map(([c, l, cls]) => (
          <button key={c} type="button" onMouseDown={(e) => { e.preventDefault(); cmd(c); }} data-testid={`${testId}-${c}`}
            className={`h-7 w-8 rounded text-slate-200 hover:bg-slate-700 text-sm ${cls}`}>{l}</button>
        ))}
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning data-testid={testId}
        onInput={(e) => { htmlRef.current = e.currentTarget.innerHTML; }}
        className="min-h-[130px] px-3 py-2 text-sm text-slate-100 focus:outline-none leading-relaxed" />
    </div>
  );
}

// Mappa un record anagrafica -> campi del form (con fallback sugli schemi alternativi).
const RAPPR_KEYS = ["rappr_cognome", "rappr_nome", "rappr_sesso", "rappr_data_nascita", "rappr_luogo_nascita", "rappr_provincia_nascita", "rappr_codice_fiscale", "rappr_indirizzo", "rappr_numero_civico", "rappr_comune", "rappr_provincia", "rappr_cap", "rappr_cellulare", "rappr_cellulari_extra", "rappr_cellulari_etichette", "rappr_email"];
const rapprOf = (o) => Object.fromEntries(RAPPR_KEYS.map((k) => [k, (o && o[k]) || ""]));
const isAzienda = (v) => v === "SI" || v === "E";
const comuniLoader = async (q) => { if (!q || q.length < 2) return []; return searchComuni(q, 30).map((c) => ({ v: c.nome, l: `${c.nome} (${c.provincia})`, prov: c.provincia, cap: c.cap || "" })); };

// Scheda soggetto IVA: tab "Dati Aziendali" + tab "Amministratore / Rappresentante".
function AziendaTabs({ f, set, setF, testId, rsKey = "ragione_sociale", similar }) {
  const generaCFRappr = () => {
    const r = buildCF({ cognome: f.rappr_cognome, nome: f.rappr_nome, sesso: f.rappr_sesso, data_nascita: f.rappr_data_nascita, comune_nascita: f.rappr_luogo_nascita, provincia_nascita: f.rappr_provincia_nascita });
    if (r.error) { toast.error(r.error); return; }
    set("rappr_codice_fiscale", r.cf); toast.success("Codice fiscale generato");
  };
  const tabCls = "data-[state=active]:bg-amber-500 data-[state=active]:text-[#0a0d14] text-slate-600 font-semibold";
  return (
    <Tabs defaultValue="azienda" className="w-full" data-testid={`${testId}-azienda-tabs`}>
      <TabsList className="bg-slate-100 border border-slate-300 flex-wrap h-auto">
        <TabsTrigger value="azienda" className={tabCls} data-testid={`${testId}-tab-azienda`}>Dati Aziendali</TabsTrigger>
        <TabsTrigger value="rappr" className={tabCls} data-testid={`${testId}-tab-rappr`}>Amministratore / Rappresentante</TabsTrigger>
      </TabsList>
      <TabsContent value="azienda" className="mt-3">
        <div className="grid sm:grid-cols-4 gap-3">
          <TF label="Ragione Sociale" value={f[rsKey]} onChange={(v) => set(rsKey, v)} testId={`${testId}-rs`} w2 />
          <TF label="Partita IVA" value={f.partita_iva} onChange={(v) => set("partita_iva", v.toUpperCase())} testId={`${testId}-piva`} mono />
          <TF label="Codice Fiscale azienda" value={f.codice_fiscale} onChange={(v) => set("codice_fiscale", v.toUpperCase())} testId={`${testId}-cf`} mono />
          {similar && <div className="sm:col-span-4">{similar}</div>}
          <TF label="Codice Destinatario SDI" value={f.codice_destinatario} onChange={(v) => set("codice_destinatario", v.toUpperCase())} testId={`${testId}-cod-dest`} mono />
          <TF label="PEC" value={f.pec} onChange={(v) => set("pec", v)} testId={`${testId}-pec`} w2 />
          <div />
          <div className="sm:col-span-2">
            <Label className={LBL}>Indirizzo sede legale</Label>
            <div className="mt-1.5"><AddressAutocomplete value={f.indirizzo} onChange={(v) => set("indirizzo", v)} onPick={(p) => setF((s) => ({ ...s, indirizzo: p.indirizzo, comune: p.comune, cap: p.cap, provincia: p.provincia }))} city={f.comune} prov={f.provincia} testId={`${testId}-indirizzo`} /></div>
          </div>
          <TF label="N° Civ." value={f.numero_civico} onChange={(v) => set("numero_civico", v)} testId={`${testId}-civico`} />
          <div><Label className={LBL}>CAP</Label><Input value={f.cap || ""} onChange={(e) => set("cap", e.target.value)} data-testid={`${testId}-cap`} className={`${INPUT_CLS} font-mono`} /></div>
          <Combobox label="Comune" value={f.comune} onChange={(v) => set("comune", v)} onSelectOption={(o) => setF((s) => ({ ...s, provincia: o.prov || "", cap: o.cap || s.cap }))} allowFree testId={`${testId}-comune`} loader={comuniLoader} />
          <div><Label className={LBL}>Provincia</Label><Input value={f.provincia || ""} onChange={(e) => set("provincia", e.target.value.toUpperCase())} data-testid={`${testId}-provincia`} className={`${INPUT_CLS} font-mono`} /></div>
          <TF label="Tel. Ufficio" value={f.tel_ufficio} onChange={(v) => set("tel_ufficio", v)} testId={`${testId}-teluff`} />
          <TF label="Email azienda" value={f.email} onChange={(v) => set("email", v)} testId={`${testId}-email`} />
        </div>
      </TabsContent>
      <TabsContent value="rappr" className="mt-3">
        <div className="grid sm:grid-cols-4 gap-3">
          <TF label="Cognome" value={f.rappr_cognome} onChange={(v) => set("rappr_cognome", v)} testId={`${testId}-rappr-cognome`} />
          <TF label="Nome" value={f.rappr_nome} onChange={(v) => set("rappr_nome", v)} testId={`${testId}-rappr-nome`} />
          <Combobox label="Sesso" value={f.rappr_sesso} onChange={(v) => set("rappr_sesso", v)} options={SESSO} testId={`${testId}-rappr-sesso`} />
          <DateField label="Data di Nascita" value={f.rappr_data_nascita} onChange={(v) => set("rappr_data_nascita", v)} testId={`${testId}-rappr-nascita`} />
          <Combobox label="Comune di Nascita" value={f.rappr_luogo_nascita} onChange={(v) => set("rappr_luogo_nascita", v)} onSelectOption={(o) => set("rappr_provincia_nascita", o.prov || "")} allowFree testId={`${testId}-rappr-comune-nascita`} loader={comuniLoader} />
          <div><Label className={LBL}>Prov. Nascita</Label><Input value={f.rappr_provincia_nascita || ""} onChange={(e) => set("rappr_provincia_nascita", e.target.value.toUpperCase())} data-testid={`${testId}-rappr-prov-nascita`} className={`${INPUT_CLS} font-mono`} /></div>
          <div className="sm:col-span-2">
            <Label className={LBL}>Codice Fiscale personale</Label>
            <div className="flex gap-2 mt-1.5">
              <Input value={f.rappr_codice_fiscale || ""} onChange={(e) => set("rappr_codice_fiscale", e.target.value.toUpperCase())} data-testid={`${testId}-rappr-cf`} className="h-9 bg-slate-900/60 border-slate-700 font-mono" />
              <Button type="button" onClick={generaCFRappr} data-testid={`${testId}-rappr-genera-cf`} className="bg-violet-600 hover:bg-violet-700 text-white shrink-0"><Sparkles className="h-4 w-4 mr-1" /> Genera CF</Button>
              <DecodificaCFButton cf={f.rappr_codice_fiscale} testId={`${testId}-rappr-decodifica-cf`} className="h-9 shrink-0" onDecode={(d) => { set("rappr_sesso", d.sesso === "F" ? "Femmina" : "Maschio"); set("rappr_data_nascita", d.data_nascita); set("rappr_luogo_nascita", d.comune_nascita); set("rappr_provincia_nascita", d.provincia_nascita); }} />
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label className={LBL}>Indirizzo</Label>
            <div className="mt-1.5"><AddressAutocomplete value={f.rappr_indirizzo} onChange={(v) => set("rappr_indirizzo", v)} onPick={(p) => setF((s) => ({ ...s, rappr_indirizzo: p.indirizzo, rappr_comune: p.comune, rappr_cap: p.cap, rappr_provincia: p.provincia }))} city={f.rappr_comune} prov={f.rappr_provincia} testId={`${testId}-rappr-indirizzo`} /></div>
          </div>
          <TF label="N° Civ." value={f.rappr_numero_civico} onChange={(v) => set("rappr_numero_civico", v)} testId={`${testId}-rappr-civico`} />
          <div><Label className={LBL}>CAP</Label><Input value={f.rappr_cap || ""} onChange={(e) => set("rappr_cap", e.target.value)} data-testid={`${testId}-rappr-cap`} className={`${INPUT_CLS} font-mono`} /></div>
          <Combobox label="Comune" value={f.rappr_comune} onChange={(v) => set("rappr_comune", v)} onSelectOption={(o) => setF((s) => ({ ...s, rappr_provincia: o.prov || "", rappr_cap: o.cap || s.rappr_cap }))} allowFree testId={`${testId}-rappr-comune`} loader={comuniLoader} />
          <div><Label className={LBL}>Provincia</Label><Input value={f.rappr_provincia || ""} onChange={(e) => set("rappr_provincia", e.target.value.toUpperCase())} data-testid={`${testId}-rappr-provincia`} className={`${INPUT_CLS} font-mono`} /></div>
          <TF label="Cellulare" value={f.rappr_cellulare} onChange={(v) => set("rappr_cellulare", v)} testId={`${testId}-rappr-cell`} wa extra={f.rappr_cellulari_extra || []} onExtra={(v) => set("rappr_cellulari_extra", v)} labels={f.rappr_cellulari_etichette || []} onLabels={(v) => set("rappr_cellulari_etichette", v)} />
          <TF label="Email personale" value={f.rappr_email} onChange={(v) => set("rappr_email", v)} testId={`${testId}-rappr-email`} />
        </div>
      </TabsContent>
    </Tabs>
  );
}

function mapRecordToForm(r) {
  if (!r) return { soggetto_iva: "NO", sesso: "" };
  const v0 = (r.veicoli && r.veicoli[0]) || {};
  return {
    soggetto_iva: isAzienda(r.soggetto_iva) ? "SI" : (r.soggetto_iva || ((r.ragione_sociale || r.denominazione || r.partita_iva) && !(r.cognome || r.nome) ? "SI" : "NO")),
    ...rapprOf(r),
    cognome: r.cognome || "", nome: r.nome || "", ragione_sociale: r.ragione_sociale || r.denominazione || "", titolo: r.titolo || "",
    sesso: r.sesso || "", data_nascita: r.data_nascita || "",
    comune_nascita: r.luogo_nascita || r.comune_nascita || "", provincia_nascita: r.provincia_nascita || r.comune_nascita_prov || "",
    codice_fiscale: r.codice_fiscale || "", partita_iva: r.partita_iva || "",
    indirizzo: r.indirizzo || "", numero_civico: r.numero_civico || "", cap: r.cap || "", comune: r.comune || "", provincia: r.provincia || "",
    cellulare: r.cellulare || "", tel_casa: r.telefono || r.tel_casa || "", tel_ufficio: r.tel_ufficio || "", fax: r.fax || "",
    cellulari_extra: r.cellulari_extra || [], cellulari_etichette: r.cellulari_etichette || [],
    email: r.email || "", pec: r.pec || "", codice_destinatario: r.codice_destinatario || "", iban: r.iban || "",
    documento: r.documento || r.tipo_documento || "", numero_doc: r.numero_doc || r.numero_documento || "",
    luogo_rilascio_doc: r.luogo_rilascio_doc || r.ente_rilascio || "",
    data_emissione_doc: r.data_emissione_doc || r.data_rilascio || "", data_scadenza_doc: r.data_scadenza_doc || "",
    costruttore: r.costruttore || v0.marca || "", modello: r.modello || v0.modello || "", colore: r.colore || v0.colore || "",
    targa: r.targa || v0.targa || "", numero_polizza: r.numero_polizza || v0.numero_polizza || "",
    compagnia: r.compagnia || r.assicurazione || v0.compagnia || "",
    scadenza_polizza: r.scadenza_polizza || v0.scadenza_polizza || "",
    data_immatricolazione: r.data_immatricolazione || v0.anno_immatricolazione || "", ruolo: r.ruolo || "",
    veicoli: Array.isArray(r.veicoli) && r.veicoli.length
      ? r.veicoli
      : ((r.targa || r.costruttore || r.modello || r.numero_polizza)
        ? [{ targa: r.targa || "", marca: r.costruttore || "", modello: r.modello || "", anno_immatricolazione: r.data_immatricolazione || "", numero_polizza: r.numero_polizza || "", compagnia: r.compagnia || r.assicurazione || "", scadenza_polizza: r.scadenza_polizza || "", tipo_veicolo: r.tipo_veicolo || "" }]
        : []),
  };
}

// ---- Form inline nuovo/esistente cliente / controparte (scheda anagrafica completa) ----
// Categoria anagrafica in cui salvare/cercare ogni tipo di soggetto della pratica.
const KIND_ATYPE = { cliente: "clienti", controparte: "controparti", testimone: "testimoni" };

function ClienteForm({ initial, dataSinistro, tipoPratica, subjectRole, onSaved, onCancel, testId, atype = "clienti" }) {
  const [f, setF] = useState(() => mapRecordToForm(initial));
  const [saving, setSaving] = useState(false);
  const editId = initial?.id || null;
  const initVeicoliRef = useRef(new Set(((initial && initial.veicoli) || []).map((v) => JSON.stringify(v || {}))));
  const set = (k, v) => setF((s) => withCfSesso(s, k, v));
  const azienda = isAzienda(f.soggetto_iva);

  const eta = useMemo(() => {
    if (!f.data_nascita || !dataSinistro) return "";
    const nb = new Date(f.data_nascita);
    const sx = new Date(dataSinistro);
    if (isNaN(nb.getTime()) || isNaN(sx.getTime())) return "";
    let a = sx.getFullYear() - nb.getFullYear();
    const mm = sx.getMonth() - nb.getMonth();
    if (mm < 0 || (mm === 0 && sx.getDate() < nb.getDate())) a--;
    return a >= 0 && a < 130 ? String(a) : "";
  }, [f.data_nascita, dataSinistro]);

  const generaCF = () => {
    const r = buildCF(f);
    if (r.error) { toast.error(r.error); return; }
    set("codice_fiscale", r.cf);
    toast.success("Codice fiscale generato");
  };

  const salva = async () => {
    if (azienda ? !f.ragione_sociale : (!f.cognome && !f.nome)) { toast.error("Inserisci il nominativo"); return; }
    setSaving(true);
    const v0 = (f.veicoli && f.veicoli[0]) || {};
    const payload = {
      soggetto_iva: f.soggetto_iva, titolo: f.titolo || "", cognome: f.cognome || "", nome: f.nome || "", ragione_sociale: f.ragione_sociale || "",
      sesso: f.sesso || "", data_nascita: f.data_nascita || "", luogo_nascita: f.comune_nascita || "", provincia_nascita: f.provincia_nascita || "",
      comune_nascita: f.comune_nascita || "", comune_nascita_prov: f.provincia_nascita || "",
      codice_fiscale: f.codice_fiscale || "", partita_iva: f.partita_iva || "",
      indirizzo: f.indirizzo || "", numero_civico: f.numero_civico || "", cap: f.cap || "", comune: f.comune || "", provincia: f.provincia || "",
      cellulare: f.cellulare || "", telefono: f.tel_casa || "", tel_ufficio: f.tel_ufficio || "", fax: f.fax || "",
      cellulari_extra: f.cellulari_extra || [], cellulari_etichette: f.cellulari_etichette || [],
      email: f.email || "", pec: f.pec || "", codice_destinatario: f.codice_destinatario || "", iban: f.iban || "",
      documento: f.documento || "", numero_doc: f.numero_doc || "", luogo_rilascio_doc: f.luogo_rilascio_doc || "",
      data_emissione_doc: f.data_emissione_doc || "", data_scadenza_doc: f.data_scadenza_doc || "",
      ...rapprOf(f),
      veicoli: (f.veicoli || []).map((v) => ({ ...v, targa: (v.targa || "").toUpperCase() })),
      costruttore: v0.marca || f.costruttore || "", modello: v0.modello || f.modello || "", colore: v0.colore || f.colore || "", targa: ((v0.targa || f.targa) || "").toUpperCase(),
      numero_polizza: v0.numero_polizza || f.numero_polizza || "", compagnia: f.compagnia || v0.compagnia || "", scadenza_polizza: v0.scadenza_polizza || f.scadenza_polizza || "",
      data_immatricolazione: v0.anno_immatricolazione || f.data_immatricolazione || "", ruolo: f.ruolo || "",
    };
    try {
      // Veicolo di QUESTA pratica: l'ultimo inserito/modificato nel wizard, altrimenti il primo della scheda.
      const vs = payload.veicoli || [];
      const changed = vs.filter((v) => v && (v.targa || v.marca || v.modello) && !initVeicoliRef.current.has(JSON.stringify(v)));
      const pv = changed.length ? changed[changed.length - 1] : (vs[0] || null);
      const guard = await guardTarga(((pv && pv.targa) || payload.targa || "").toUpperCase(), editId);
      if (guard.action === "existing" && guard.match?.record) {
        onSaved({ ...guard.match.record });
        setSaving(false);
        return;
      }
      let targetId = editId;
      const cf = (payload.codice_fiscale || "").trim().toUpperCase();
      if (!targetId && cf) {
        // Controllo omonimie: evita inserimenti doppi cercando il CF già presente in anagrafica.
        try {
          const { data: found } = await api.get(`/anagrafiche/${atype}?q=${encodeURIComponent(cf)}`);
          const match = (found || []).find((x) => (x.codice_fiscale || "").trim().toUpperCase() === cf);
          if (match) targetId = match.id;
        } catch { /* ignore */ }
      }
      let data;
      if (targetId) {
        const res = await api.patch(`/anagrafiche/${atype}/${targetId}`, payload);
        data = res.data; toast.success("Anagrafica aggiornata");
      } else {
        const { action, record } = await guardCreate(atype, payload);
        data = record; if (action === "created") toast.success("Soggetto salvato in anagrafica");
      }
      onSaved({ ...(data || {}), ...payload, id: (data && data.id) || targetId, veicolo_pratica: pv });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-md border border-teal-600/40 bg-teal-500/5 p-4 space-y-4" data-testid={`${testId}-form`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-head font-bold text-teal-300 tracking-wide">{editId ? "SCHEDA ANAGRAFICA" : "NUOVO — SCHEDA ANAGRAFICA"}</span>
        <button type="button" onClick={onCancel} data-testid={`${testId}-close`} className="text-slate-400 hover:text-slate-200"><X className="h-4 w-4" /></button>
      </div>

      <div className="flex items-center gap-4">
        <Label className={LBL}>Soggetto IVA</Label>
        <div className="flex rounded-md overflow-hidden border border-slate-700">
          {["SI", "NO"].map((o) => (
            <button key={o} type="button" onClick={() => set("soggetto_iva", o)} data-testid={`${testId}-iva-${o}`}
              className={`px-4 py-1.5 text-sm font-semibold ${(o === "SI" ? azienda : !azienda) ? "bg-amber-500 text-[#0a0d14]" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>{o}</button>
          ))}
        </div>
      </div>

      {azienda ? (<>
        <AziendaTabs f={f} set={set} setF={setF} testId={testId}
          similar={!editId ? <SimilarContacts atype={atype} denominazione={f.ragione_sociale} onSelect={(r) => onSaved({ ...r })} onCancel={onCancel} testId={`${testId}-similar`} /> : null} />
        <div className="grid sm:grid-cols-4 gap-3">
          <TF label="IBAN" value={f.iban} onChange={(v) => set("iban", v.toUpperCase())} testId={`${testId}-iban`} mono w2 />
        </div>
      </>) : (<>
      <div className="grid sm:grid-cols-3 gap-3">
        {(<>
          <Combobox label="Titolo" value={f.titolo} onChange={(v) => set("titolo", v)} options={TITOLI} allowFree testId={`${testId}-titolo`} />
          <TF label="Cognome" value={f.cognome} onChange={(v) => set("cognome", v)} testId={`${testId}-cognome`} />
          <TF label="Nome" value={f.nome} onChange={(v) => set("nome", v)} testId={`${testId}-nome`} />
        </>)}
        {!editId && <div className="sm:col-span-3"><SimilarContacts atype={atype} nome={azienda ? "" : f.nome} cognome={azienda ? "" : f.cognome} denominazione={azienda ? f.ragione_sociale : ""} onSelect={(r) => onSaved({ ...r })} onCancel={onCancel} testId={`${testId}-similar`} /></div>}
        <Combobox label="Sesso" value={f.sesso} onChange={(v) => set("sesso", v)} options={SESSO} testId={`${testId}-sesso`} />
        <DateField label="Data di Nascita" value={f.data_nascita} onChange={(v) => set("data_nascita", v)} testId={`${testId}-nascita`} />
        <Combobox label="Comune di Nascita" value={f.comune_nascita} onChange={(v) => set("comune_nascita", v)} onSelectOption={(o) => set("provincia_nascita", o.prov || "")} allowFree testId={`${testId}-comune-nascita`}
          loader={async (q) => { if (!q || q.length < 2) return []; return searchComuni(q, 30).map((c) => ({ v: c.nome, l: `${c.nome} (${c.provincia})`, prov: c.provincia, cap: c.cap || "" })); }} />
        <div><Label className={LBL}>Prov. Nascita</Label><Input value={f.provincia_nascita || ""} readOnly data-testid={`${testId}-prov-nascita`} className={`${INPUT_CLS} font-mono bg-slate-800/60`} placeholder="—" title="Si popola dal comune di nascita" /></div>
        <div className="sm:col-span-2">
          <Label className={LBL}>{azienda ? "Partita IVA" : "Codice Fiscale"}</Label>
          <div className="flex gap-2 mt-1.5">
            <Input value={(azienda ? f.partita_iva : f.codice_fiscale) || ""} onChange={(e) => set(azienda ? "partita_iva" : "codice_fiscale", e.target.value.toUpperCase())} data-testid={`${testId}-cf`} className="h-9 bg-slate-900/60 border-slate-700 font-mono" />
            {!azienda && <Button type="button" onClick={generaCF} data-testid={`${testId}-genera-cf`} className="bg-violet-600 hover:bg-violet-700 text-white shrink-0"><Sparkles className="h-4 w-4 mr-1" /> Genera CF</Button>}
            {!azienda && <DecodificaCFButton cf={f.codice_fiscale} testId={`${testId}-decodifica-cf`} className="h-9 shrink-0" onDecode={(d) => { set("sesso", d.sesso === "F" ? "Femmina" : "Maschio"); set("data_nascita", d.data_nascita); set("comune_nascita", d.comune_nascita); set("provincia_nascita", d.provincia_nascita); if (!f.cognome) set("cognome", d.cognomeCode); if (!f.nome) set("nome", d.nomeCode); }} />}
          </div>
        </div>
        <div>
          <Label className={LBL}>Età in data SX</Label>
          <Input value={eta} readOnly data-testid={`${testId}-eta`} className={`${INPUT_CLS} font-mono bg-slate-800/60`} />
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-3">
        <div className="sm:col-span-2">
          <Label className={LBL}>Indirizzo</Label>
          <div className="mt-1.5"><AddressAutocomplete value={f.indirizzo} onChange={(v) => set("indirizzo", v)} onPick={(p) => setF((s) => ({ ...s, indirizzo: p.indirizzo, comune: p.comune, cap: p.cap, provincia: p.provincia }))} city={f.comune} prov={f.provincia} testId={`${testId}-indirizzo`} /></div>
        </div>
        <TF label="N° Civ." value={f.numero_civico} onChange={(v) => set("numero_civico", v)} testId={`${testId}-civico`} />
        <div><Label className={LBL}>CAP</Label><Input value={f.cap || ""} onChange={(e) => set("cap", e.target.value)} data-testid={`${testId}-cap`} className={`${INPUT_CLS} font-mono`} /></div>
        <Combobox label="Comune" value={f.comune} onChange={(v) => set("comune", v)} onSelectOption={(o) => setF((s) => ({ ...s, provincia: o.prov || "", cap: o.cap || s.cap }))} allowFree testId={`${testId}-comune`}
          loader={async (q) => { if (!q || q.length < 2) return []; return searchComuni(q, 30).map((c) => ({ v: c.nome, l: `${c.nome} (${c.provincia})`, prov: c.provincia, cap: c.cap || "" })); }} />
        <div><Label className={LBL}>Provincia</Label><Input value={f.provincia || ""} onChange={(e) => set("provincia", e.target.value.toUpperCase())} data-testid={`${testId}-provincia`} className={`${INPUT_CLS} font-mono`} /></div>
        <TF label="Cellulare" value={f.cellulare} onChange={(v) => set("cellulare", v)} testId={`${testId}-cell`} wa extra={f.cellulari_extra} onExtra={(v) => set("cellulari_extra", v)} labels={f.cellulari_etichette} onLabels={(v) => set("cellulari_etichette", v)} />
        <TF label="Tel. Casa" value={f.tel_casa} onChange={(v) => set("tel_casa", v)} testId={`${testId}-telcasa`} />
        <TF label="Tel. Ufficio" value={f.tel_ufficio} onChange={(v) => set("tel_ufficio", v)} testId={`${testId}-teluff`} />
        <TF label="Fax" value={f.fax} onChange={(v) => set("fax", v)} testId={`${testId}-fax`} />
        <TF label="Email" value={f.email} onChange={(v) => set("email", v)} testId={`${testId}-email`} w2 />
        <TF label="PEC" value={f.pec} onChange={(v) => set("pec", v)} testId={`${testId}-pec`} w2 />
        <TF label="Codice Destinatario" value={f.codice_destinatario} onChange={(v) => set("codice_destinatario", v)} testId={`${testId}-cod-dest`} mono />
        <TF label="IBAN" value={f.iban} onChange={(v) => set("iban", v.toUpperCase())} testId={`${testId}-iban`} mono w2 />
      </div>
      </>)}

      <div className="grid sm:grid-cols-4 gap-3">
        <Combobox label="Tipo Documento" value={f.documento} onChange={(v) => set("documento", v)} options={DOCUMENTI} testId={`${testId}-doc`} />
        <TF label="Numero Documento" value={f.numero_doc} onChange={(v) => set("numero_doc", v)} testId={`${testId}-numdoc`} mono />
        <TF label="Luogo Rilascio" value={f.luogo_rilascio_doc} onChange={(v) => set("luogo_rilascio_doc", v)} testId={`${testId}-luogodoc`} />
        <div />
        <DateField label="Data Rilascio" value={f.data_emissione_doc} onChange={(v) => set("data_emissione_doc", v)} testId={`${testId}-datadoc`} />
        <DateField label="Data Scadenza" value={f.data_scadenza_doc} onChange={(v) => set("data_scadenza_doc", v)} testId={`${testId}-scaddoc`} />
      </div>

      {veicoloVisibile(tipoPratica) && <div className="rounded-md border border-slate-700 p-3">
        <div className="text-[11px] uppercase tracking-wider text-orange-300 font-mono mb-3">Veicoli</div>
        <VeicoliEditor veicoli={f.veicoli || []} onChange={(arr) => set("veicoli", arr)} hideHeader />
        {(f.veicoli || []).some((v) => v && v.targa && !isValidTarga(v.targa)) && (
          <p className="mt-2 text-[11px] text-amber-400" data-testid={`${testId}-targa-warning`}>Una o più targhe hanno un formato non valido — puoi comunque proseguire.</p>
        )}
      </div>}

      <div className="flex justify-end">
        <Button type="button" onClick={salva} disabled={saving} data-testid={`${testId}-salva`} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editId ? "Aggiorna Cliente" : "Salva Cliente")}
        </Button>
      </div>
    </div>
  );
}

// ---- Form inline testimone (semplificato) — nuovo o esistente ----
function TestimoneForm({ initial, onSaved, onCancel, testId }) {
  const [f, setF] = useState(() => ({ cognome: initial?.cognome || "", nome: initial?.nome || "", sesso: initial?.sesso || "", codice_fiscale: initial?.codice_fiscale || "", data_nascita: initial?.data_nascita || "" }));
  const [saving, setSaving] = useState(false);
  const editId = initial?.id || null;
  const set = (k, v) => setF((s) => withCfSesso(s, k, v));
  const salva = async () => {
    if (!f.cognome && !f.nome) { toast.error("Inserisci il nominativo"); return; }
    const base = { cognome: f.cognome || "", nome: f.nome || "", sesso: f.sesso || "", codice_fiscale: f.codice_fiscale || "", data_nascita: f.data_nascita || "", nominativo: [f.cognome, f.nome].filter(Boolean).join(" ") };
    if (editId) {
      setSaving(true);
      try { await api.patch(`/anagrafiche/testimoni/${editId}`, { cognome: base.cognome, nome: base.nome, sesso: base.sesso, codice_fiscale: base.codice_fiscale, data_nascita: base.data_nascita }); toast.success("Anagrafica aggiornata"); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setSaving(false); }
      onSaved({ ...base, id: editId });
    } else { onSaved(base); }
  };
  return (
    <div className="rounded-md border border-teal-600/40 bg-teal-500/5 p-4 space-y-4" data-testid={`${testId}-form`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-head font-bold text-teal-300 tracking-wide">{editId ? "TESTIMONE" : "NUOVO TESTIMONE"}</span>
        <button type="button" onClick={onCancel} data-testid={`${testId}-close`} className="text-slate-400 hover:text-slate-200"><X className="h-4 w-4" /></button>
      </div>
      <div className="grid sm:grid-cols-5 gap-3">
        <TF label="Cognome" value={f.cognome} onChange={(v) => set("cognome", v)} testId={`${testId}-cognome`} />
        <TF label="Nome" value={f.nome} onChange={(v) => set("nome", v)} testId={`${testId}-nome`} />
        {!editId && <div className="sm:col-span-5"><SimilarContacts atype="testimoni" nome={f.nome} cognome={f.cognome} onSelect={(r) => onSaved({ ...r, nominativo: [r.cognome, r.nome].filter(Boolean).join(" ") })} onCancel={onCancel} testId={`${testId}-similar`} /></div>}
        <Combobox label="Sesso" value={f.sesso} onChange={(v) => set("sesso", v)} options={SESSO} testId={`${testId}-sesso`} />
        <TF label="Codice Fiscale" value={f.codice_fiscale} onChange={(v) => set("codice_fiscale", v.toUpperCase())} testId={`${testId}-cf`} mono />
        <div className="flex items-end">
          <DecodificaCFButton cf={f.codice_fiscale} testId={`${testId}-decodifica-cf`} className="h-9 w-full justify-center" onDecode={(d) => { set("sesso", d.sesso === "F" ? "Femmina" : "Maschio"); set("data_nascita", d.data_nascita); if (!f.cognome) set("cognome", d.cognomeCode); if (!f.nome) set("nome", d.nomeCode); }} />
        </div>
        <DateField label="Data Nascita" value={f.data_nascita} onChange={(v) => set("data_nascita", v)} testId={`${testId}-nascita`} />
      </div>
      <div className="flex justify-end">
        <Button type="button" onClick={salva} disabled={saving} data-testid={`${testId}-salva`} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editId ? "Aggiorna Testimone" : "Salva Testimone")}</Button>
      </div>
    </div>
  );
}

// ---- Form inline "Crea nuovo" (cliente/controparte/testimone) ----
function NewSubjectForm({ kind, initialName, initial, onSaved, onCancel, testId, tipoPratica = "" }) {
  const [f, setF] = useState(() => {
    if (initial) return { soggetto_iva: "NO", ...buildTestimone(initial) };
    const nm = (initialName || "").trim();
    const parts = nm.split(/\s+/).filter(Boolean);
    return { soggetto_iva: "NO", sesso: "", cognome: parts[0] || "", nome: parts.slice(1).join(" ") || "", denominazione: nm };
  });
  const [veicolo, setVeicolo] = useState(null);
  const [veicoli, setVeicoli] = useState([]);
  const [cfManual, setCfManual] = useState(!!initial?.codice_fiscale);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => withCfSesso(s, k, v));
  const azienda = isAzienda(f.soggetto_iva);
  const isTest = kind === "testimone";
  const editId = initial ? (initial.id || initial.cliente_id || null) : null;
  const titolo = kind === "controparte" ? "NUOVA CONTROPARTE" : isTest ? (initial ? "TESTIMONE" : "NUOVO TESTIMONE") : "NUOVO CLIENTE";

  const generaCF = () => {
    const r = buildCF(f);
    if (r.error) { toast.error(r.error); return; }
    set("codice_fiscale", r.cf); toast.success("Codice fiscale generato");
  };

  // Testimone: calcolo del codice fiscale in tempo reale (salvo override manuale dell'utente).
  useEffect(() => {
    if (!isTest || cfManual) return;
    const { cognome, nome, sesso, data_nascita, comune_nascita, comune_nascita_prov } = f;
    if (!(cognome && nome && sesso && data_nascita && comune_nascita)) return;
    try {
      const [y, m, d] = (data_nascita || "").split("-").map(Number);
      const cf = new CodiceFiscale({ name: nome, surname: cognome, gender: sesso === "Femmina" ? "F" : "M", day: d, month: m, year: y, birthplace: comune_nascita, birthplaceProvincia: comune_nascita_prov || undefined }).toString();
      if (cf && cf !== f.codice_fiscale) setF((s) => ({ ...s, codice_fiscale: cf }));
    } catch { /* dati insufficienti o comune non riconosciuto */ }
  }, [isTest, cfManual, f.cognome, f.nome, f.sesso, f.data_nascita, f.comune_nascita, f.comune_nascita_prov]); // eslint-disable-line react-hooks/exhaustive-deps

  const salva = async () => {
    if (isTest) { if (!f.cognome && !f.nome) { toast.error("Inserisci il nominativo"); return; } }
    else if (azienda ? !f.denominazione : (!f.cognome || !f.nome)) { toast.error(azienda ? "Inserisci la denominazione" : "Cognome e Nome sono obbligatori"); return; }
    setSaving(true);
    const payload = isTest ? {
      soggetto_iva: "NO", titolo: f.titolo || "", cognome: f.cognome || "", nome: f.nome || "", sesso: f.sesso || "", codice_fiscale: f.codice_fiscale || "",
      data_nascita: f.data_nascita || "", comune_nascita: f.comune_nascita || "", comune_nascita_prov: f.comune_nascita_prov || "",
      luogo_nascita: f.comune_nascita || "", provincia_nascita: f.comune_nascita_prov || "",
      professione: f.professione || "", cellulare: f.cellulare || "", telefono: f.telefono || f.cellulare || "",
      cellulari_extra: f.cellulari_extra || [], cellulari_etichette: f.cellulari_etichette || [],
      indirizzo: f.indirizzo || "", numero_civico: f.numero_civico || "", comune: f.comune || "", cap: f.cap || "", provincia: f.provincia || "",
      tipo_documento: f.tipo_documento || "", numero_documento: f.numero_documento || "", documento_rilasciato_da: f.documento_rilasciato_da || "",
      doc_luogo_rilascio: f.doc_luogo_rilascio || "", doc_data_rilascio: f.doc_data_rilascio || "", doc_data_scadenza: f.doc_data_scadenza || "",
    } : {
      soggetto_iva: f.soggetto_iva, titolo: f.titolo || "",
      cognome: f.cognome || "", nome: f.nome || "", ragione_sociale: azienda ? (f.denominazione || "") : [f.cognome, f.nome].filter(Boolean).join(" "), denominazione: azienda ? (f.denominazione || "") : "",
      sesso: f.sesso || "", data_nascita: f.data_nascita || "", luogo_nascita: f.comune_nascita || "", provincia_nascita: f.provincia_nascita || "",
      comune_nascita: f.comune_nascita || "", comune_nascita_prov: f.provincia_nascita || "",
      codice_fiscale: f.codice_fiscale || "", partita_iva: f.partita_iva || "",
      indirizzo: f.indirizzo || "", numero_civico: f.numero_civico || "", cap: f.cap || "", comune: f.comune || "", provincia: f.provincia || "",
      cellulare: f.cellulare || "", tel_ufficio: f.tel_ufficio || "", fax: f.fax || "", email: f.email || "", pec: f.pec || "",
      codice_destinatario: f.codice_destinatario || "", ...rapprOf(f),
      cellulari_extra: f.cellulari_extra || [], cellulari_etichette: f.cellulari_etichette || [],
    };
    if (kind === "controparte" && veicolo) { payload.veicolo = veicolo; payload.veicoli = [veicolo]; }
    if (kind === "cliente" && !isTest && veicoli.length) {
      const v0 = veicoli[0] || {};
      payload.veicoli = veicoli.map((v) => ({ ...v, targa: (v.targa || "").toUpperCase() }));
      payload.costruttore = v0.marca || ""; payload.modello = v0.modello || ""; payload.colore = v0.colore || ""; payload.targa = (v0.targa || "").toUpperCase();
      payload.numero_polizza = v0.numero_polizza || ""; payload.compagnia = payload.compagnia || v0.compagnia || "";
      payload.scadenza_polizza = v0.scadenza_polizza || ""; payload.data_immatricolazione = v0.anno_immatricolazione || "";
    }
    try {
      if (isTest && editId) {
        await api.patch(`/anagrafiche/testimoni/${editId}`, payload);
        toast.success("Testimone aggiornato");
        onSaved({ ...payload, id: editId });
        return;
      }
      const { action, record } = await guardCreate(KIND_ATYPE[kind] || "clienti", payload);
      if (action === "created") toast.success("Soggetto salvato in anagrafica");
      onSaved({ ...(record || {}), ...payload, id: record?.id, veicolo: kind === "controparte" ? (veicolo || null) : undefined });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const comuneLoader = async (q) => { if (!q || q.length < 2) return []; return searchComuni(q, 30).map((c) => ({ v: c.nome, l: `${c.nome} (${c.provincia})`, prov: c.provincia, cap: c.cap || "" })); };

  return (
    <div className="rounded-md border border-teal-600/40 bg-teal-500/5 p-4 space-y-4" data-testid={`${testId}-form`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-head font-bold text-teal-300 tracking-wide">{titolo}</span>
        <button type="button" onClick={onCancel} data-testid={`${testId}-close`} className="text-slate-400 hover:text-slate-200"><X className="h-4 w-4" /></button>
      </div>

      {isTest ? (
        <div className="grid sm:grid-cols-3 gap-3">
          <TF label="Cognome" value={f.cognome} onChange={(v) => set("cognome", v)} testId={`${testId}-cognome`} />
          <TF label="Nome" value={f.nome} onChange={(v) => set("nome", v)} testId={`${testId}-nome`} />
          {!initial && <div className="sm:col-span-3"><SimilarContacts atype={KIND_ATYPE[kind] || "clienti"} nome={f.nome} cognome={f.cognome} onSelect={(r) => onSaved({ ...r })} onCancel={onCancel} testId={`${testId}-similar`} /></div>}
          <Combobox label="Sesso" value={f.sesso} onChange={(v) => set("sesso", v)} options={SESSO} testId={`${testId}-sesso`} />
          <DateField label="Data Nascita" value={f.data_nascita} onChange={(v) => set("data_nascita", v)} testId={`${testId}-nascita`} />
          <Combobox label="Comune di Nascita" value={f.comune_nascita} onChange={(v) => set("comune_nascita", v)} onSelectOption={(o) => set("comune_nascita_prov", o.prov || "")} allowFree testId={`${testId}-comune-nascita`} loader={comuneLoader} />
          <div><Label className={LBL}>Prov. Nascita</Label><Input value={f.comune_nascita_prov || ""} readOnly data-testid={`${testId}-prov-nascita`} className={`${INPUT_CLS} font-mono bg-slate-800/60`} placeholder="—" /></div>
          <TF label="Codice Fiscale (auto)" value={f.codice_fiscale} onChange={(v) => { set("codice_fiscale", v.toUpperCase()); setCfManual(true); }} testId={`${testId}-cf`} mono w2 />
          <TF label="Professione" value={f.professione} onChange={(v) => set("professione", v)} testId={`${testId}-professione`} />
          <TF label="Cellulare" value={f.cellulare} onChange={(v) => set("cellulare", v)} testId={`${testId}-cell`} wa extra={f.cellulari_extra} onExtra={(v) => set("cellulari_extra", v)} labels={f.cellulari_etichette} onLabels={(v) => set("cellulari_etichette", v)} />
          <TF label="Telefono" value={f.telefono} onChange={(v) => set("telefono", v)} testId={`${testId}-telefono`} />
          <div className="sm:col-span-2"><Label className={LBL}>Indirizzo</Label><div className="mt-1.5"><AddressAutocomplete value={f.indirizzo} onChange={(v) => set("indirizzo", v)} onPick={(pk) => setF((s) => ({ ...s, indirizzo: pk.indirizzo, comune: pk.comune, cap: pk.cap, provincia: pk.provincia }))} city={f.comune} prov={f.provincia} testId={`${testId}-indirizzo`} /></div></div>
          <TF label="N° Civ." value={f.numero_civico} onChange={(v) => set("numero_civico", v)} testId={`${testId}-civico`} />
          <Combobox label="Comune (residenza)" value={f.comune} onChange={(v) => set("comune", v)} onSelectOption={(o) => setF((s) => ({ ...s, provincia: o.prov || s.provincia, cap: o.cap || s.cap }))} allowFree testId={`${testId}-comune`} loader={comuneLoader} />
          <div><Label className={LBL}>CAP</Label><Input value={f.cap || ""} onChange={(e) => set("cap", e.target.value)} data-testid={`${testId}-cap`} className={`${INPUT_CLS} font-mono`} /></div>
          <div><Label className={LBL}>Provincia</Label><Input value={f.provincia || ""} onChange={(e) => set("provincia", e.target.value.toUpperCase())} data-testid={`${testId}-provincia`} className={`${INPUT_CLS} font-mono`} /></div>
          <Combobox label="Tipo Documento" value={f.tipo_documento} onChange={(v) => set("tipo_documento", v)} options={DOCUMENTI} testId={`${testId}-tipo-doc`} />
          <TF label="Numero Documento" value={f.numero_documento} onChange={(v) => set("numero_documento", v)} testId={`${testId}-num-doc`} mono />
          <TF label="Rilasciato da" value={f.documento_rilasciato_da} onChange={(v) => set("documento_rilasciato_da", v)} testId={`${testId}-doc-rilda`} />
          <TF label="Luogo Rilascio" value={f.doc_luogo_rilascio} onChange={(v) => set("doc_luogo_rilascio", v)} testId={`${testId}-doc-luogo`} />
          <DateField label="Data Rilascio" value={f.doc_data_rilascio} onChange={(v) => set("doc_data_rilascio", v)} testId={`${testId}-doc-rilascio`} />
          <DateField label="Data Scadenza" value={f.doc_data_scadenza} onChange={(v) => set("doc_data_scadenza", v)} testId={`${testId}-doc-scadenza`} />
        </div>
      ) : (
        <>
          <div className="flex items-center gap-4">
            <Label className={LBL}>Tipo Soggetto</Label>
            <div className="flex rounded-md overflow-hidden border border-slate-700">
              {[["NO", "Persona Fisica"], ["SI", "Azienda"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => set("soggetto_iva", v)} data-testid={`${testId}-tipo-${v}`}
                  className={`px-4 py-1.5 text-sm font-semibold ${f.soggetto_iva === v ? "bg-amber-500 text-[#0a0d14]" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>{l}</button>
              ))}
            </div>
          </div>

          {azienda ? (
            <AziendaTabs f={f} set={set} setF={setF} testId={testId} rsKey="denominazione"
              similar={<SimilarContacts atype={KIND_ATYPE[kind] || "clienti"} denominazione={f.denominazione} onSelect={(r) => onSaved({ ...r })} onCancel={onCancel} testId={`${testId}-similar`} />} />
          ) : (
            <div className="grid sm:grid-cols-3 gap-3">
              <Combobox label="Titolo" value={f.titolo} onChange={(v) => set("titolo", v)} options={TITOLI} allowFree testId={`${testId}-titolo`} />
              <TF label="Cognome" value={f.cognome} onChange={(v) => set("cognome", v)} testId={`${testId}-cognome`} />
              <TF label="Nome" value={f.nome} onChange={(v) => set("nome", v)} testId={`${testId}-nome`} />
              <div className="sm:col-span-3"><SimilarContacts atype={KIND_ATYPE[kind] || "clienti"} nome={f.nome} cognome={f.cognome} onSelect={(r) => onSaved({ ...r })} onCancel={onCancel} testId={`${testId}-similar`} /></div>
              <Combobox label="Sesso" value={f.sesso} onChange={(v) => set("sesso", v)} options={SESSO} testId={`${testId}-sesso`} />
              <DateField label="Data di Nascita" value={f.data_nascita} onChange={(v) => set("data_nascita", v)} testId={`${testId}-nascita`} />
              <Combobox label="Comune di Nascita" value={f.comune_nascita} onChange={(v) => set("comune_nascita", v)} onSelectOption={(o) => set("provincia_nascita", o.prov || "")} allowFree testId={`${testId}-comune-nascita`} loader={comuneLoader} />
              <div><Label className={LBL}>Prov. Nascita</Label><Input value={f.provincia_nascita || ""} readOnly data-testid={`${testId}-prov-nascita`} className={`${INPUT_CLS} font-mono bg-slate-800/60`} placeholder="—" title="Si popola dal comune di nascita" /></div>
              <div className="sm:col-span-2">
                <Label className={LBL}>Codice Fiscale</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={f.codice_fiscale || ""} onChange={(e) => set("codice_fiscale", e.target.value.toUpperCase())} data-testid={`${testId}-cf`} className="h-9 bg-slate-900/60 border-slate-700 font-mono" />
                  <Button type="button" onClick={generaCF} data-testid={`${testId}-genera-cf`} className="bg-violet-600 hover:bg-violet-700 text-white shrink-0"><Sparkles className="h-4 w-4 mr-1" /> Genera CF</Button>
                  <DecodificaCFButton cf={f.codice_fiscale} testId={`${testId}-decodifica-cf`} className="h-9 shrink-0" onDecode={(d) => { setCfManual(true); set("sesso", d.sesso === "F" ? "Femmina" : "Maschio"); set("data_nascita", d.data_nascita); set("comune_nascita", d.comune_nascita); set("provincia_nascita", d.provincia_nascita); set("comune_nascita_prov", d.provincia_nascita); }} />
                </div>
              </div>
            </div>
          )}

          {!azienda && <div className="grid sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <Label className={LBL}>Indirizzo</Label>
              <div className="mt-1.5"><AddressAutocomplete value={f.indirizzo} onChange={(v) => set("indirizzo", v)} onPick={(p) => setF((s) => ({ ...s, indirizzo: p.indirizzo, comune: p.comune, cap: p.cap, provincia: p.provincia }))} city={f.comune} prov={f.provincia} testId={`${testId}-indirizzo`} /></div>
            </div>
            <TF label="N° Civ." value={f.numero_civico} onChange={(v) => set("numero_civico", v)} testId={`${testId}-civico`} />
            <div><Label className={LBL}>CAP</Label><Input value={f.cap || ""} onChange={(e) => set("cap", e.target.value)} data-testid={`${testId}-cap`} className={`${INPUT_CLS} font-mono`} /></div>
            <Combobox label="Comune / Città" value={f.comune} onChange={(v) => set("comune", v)} onSelectOption={(o) => setF((s) => ({ ...s, provincia: o.prov || "", cap: o.cap || s.cap }))} allowFree testId={`${testId}-comune`} loader={comuneLoader} />
            <div><Label className={LBL}>Provincia</Label><Input value={f.provincia || ""} onChange={(e) => set("provincia", e.target.value.toUpperCase())} data-testid={`${testId}-provincia`} className={`${INPUT_CLS} font-mono`} /></div>
            <TF label="Cellulare" value={f.cellulare} onChange={(v) => set("cellulare", v)} testId={`${testId}-cell`} wa extra={f.cellulari_extra} onExtra={(v) => set("cellulari_extra", v)} labels={f.cellulari_etichette} onLabels={(v) => set("cellulari_etichette", v)} />
            <TF label="Tel. Ufficio" value={f.tel_ufficio} onChange={(v) => set("tel_ufficio", v)} testId={`${testId}-teluff`} />
            <TF label="Fax" value={f.fax} onChange={(v) => set("fax", v)} testId={`${testId}-fax`} />
            <TF label="Email" value={f.email} onChange={(v) => set("email", v)} testId={`${testId}-email`} />
            <TF label="PEC" value={f.pec} onChange={(v) => set("pec", v)} testId={`${testId}-pec`} w2 />
          </div>}
        </>
      )}

      {kind === "cliente" && !isTest && veicoloVisibile(tipoPratica) && (
        <div className="rounded-md border border-slate-700 p-3" data-testid={`${testId}-veicoli-wrap`}>
          <div className="text-[11px] uppercase tracking-wider text-orange-300 font-mono mb-3">Veicoli</div>
          <VeicoliEditor veicoli={veicoli} onChange={setVeicoli} hideHeader />
        </div>
      )}

      {kind === "controparte" && !isTest && veicoloVisibile(tipoPratica) && (
        <ControparteVeicolo veicolo={veicolo} onChange={setVeicolo} testId={`${testId}-veicolo`} />
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" onClick={onCancel} data-testid={`${testId}-annulla`} className="border border-slate-700 bg-slate-800 text-white hover:bg-slate-700">Annulla</Button>
        <Button type="button" onClick={salva} disabled={saving} data-testid={`${testId}-salva`} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isTest && initial ? "Aggiorna Testimone" : "Salva e aggiungi")}</Button>
      </div>
    </div>
  );
}

// ---- Sezione "Conducente del veicolo" (Step 2, per la Denuncia di Sinistro/CID) ----
function ConducenteSection({ value, onChange, subjectNome, testId = "w-conducente", checkboxLabel = "Il cliente è anche il conducente", subjectWord = "cliente", hint }) {
  const c = value || { is_cliente: true };
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [showRes, setShowRes] = useState(false);
  const [creating, setCreating] = useState(false);
  const timer = useRef(null);
  const set = (patch) => onChange(patch.codice_fiscale !== undefined ? withCfSesso({ ...c, ...patch }, "codice_fiscale", patch.codice_fiscale) : { ...c, ...patch });

  useEffect(() => {
    if (c.is_cliente) { setResults([]); setShowRes(false); return; }
    if (!q || q.trim().length < 2) { setResults([]); setShowRes(false); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get(`/anagrafiche/clienti?q=${encodeURIComponent(q)}`); setResults(data || []); setShowRes(true); }
      catch { setResults([]); }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, c.is_cliente]);

  const hasData = !!(c.cognome || c.nome || c.codice_fiscale || c.cliente_id);
  const showForm = !c.is_cliente && (creating || hasData);
  const comuneLoader = async (qq) => { if (!qq || qq.length < 2) return []; return searchComuni(qq, 30).map((x) => ({ v: x.nome, l: `${x.nome} (${x.provincia})`, prov: x.provincia, cap: x.cap || "" })); };

  const pickAnag = (r) => {
    onChange({
      is_cliente: false, cliente_id: r.id || null, nominativo: formatNominativo(r) || "",
      cognome: r.cognome || "", nome: r.nome || "", sesso: r.sesso || "",
      data_nascita: r.data_nascita || "", comune_nascita: r.luogo_nascita || r.comune_nascita || "", provincia_nascita: r.provincia_nascita || "",
      codice_fiscale: r.codice_fiscale || "",
      patente_n: r.patente_n || "", patente_categoria: r.patente_categoria || "", patente_rilascio: r.patente_rilascio || "", patente_scadenza: r.patente_scadenza || "",
      indirizzo: r.indirizzo || "", numero_civico: r.numero_civico || "", cap: r.cap || "", comune: r.comune || "", provincia: r.provincia || "",
      cellulare: r.cellulare || r.cell || "", cellulari_extra: r.cellulari_extra || [], cellulari_etichette: r.cellulari_etichette || [], email: r.email || "",
    });
    setQ(""); setResults([]); setShowRes(false); setCreating(false);
  };

  const generaCF = () => {
    const r = buildCF(c);
    if (r.error) { toast.error(r.error); return; }
    set({ codice_fiscale: r.cf }); toast.success("Codice fiscale generato");
  };

  const reset = () => { onChange({ is_cliente: false }); setCreating(false); setQ(""); };

  return (
    <div className="rounded-lg border-2 border-teal-500/50 bg-teal-500/[0.07] p-4 shadow-sm" data-testid={testId}>
      <div className="flex items-center gap-2">
        <Car className="h-5 w-5 text-teal-300 shrink-0" />
        <span className="text-sm font-head font-bold uppercase tracking-wide text-teal-200">Conducente del Veicolo</span>
      </div>
      <p className="text-xs text-slate-400 mt-1 mb-3">{hint || "Se alla guida non c'era il cliente, indica qui i dati del conducente effettivo."}</p>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm text-slate-100 font-medium">C'è un conducente per questa pratica?</span>
        <div className="flex items-center gap-1" data-testid={`${testId}-sino`}>
          <button type="button" onClick={() => onChange({ ...c, is_cliente: false })} data-testid={`${testId}-si`}
            className={`px-3 py-1 rounded text-sm border transition-colors ${c.is_cliente === false ? "bg-teal-600 border-teal-500 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-800"}`}>Sì</button>
          <button type="button" onClick={() => onChange({ is_cliente: true })} data-testid={`${testId}-no`}
            className={`px-3 py-1 rounded text-sm border transition-colors ${c.is_cliente !== false ? "bg-slate-600 border-slate-500 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-800"}`}>No</button>
        </div>
      </div>

      {c.is_cliente !== false ? (
        <p className="text-xs text-slate-400 mt-2">Alla guida c'era {subjectWord}{subjectNome ? ` (${subjectNome})` : ""}. Nessun conducente separato da indicare.</p>
      ) : (
        <div className="mt-4 space-y-4">
          <div className="rounded-md border border-slate-700 bg-slate-900/40 p-3">
            <Label className={LBL}>Cerca conducente in anagrafica</Label>
            <div className="relative mt-1.5">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setShowRes(true)}
                placeholder="Digita cognome o ragione sociale…" data-testid={`${testId}-search`} className="h-9 pl-8 bg-slate-900/60 border-slate-700" />
              {showRes && results.length > 0 && (
                <div className="absolute z-30 mt-1 w-full rounded-md border border-slate-700 bg-[#121824] shadow-lg max-h-64 overflow-y-auto" data-testid={`${testId}-results`}>
                  {results.map((r) => (
                    <button key={r.id} type="button" onClick={() => pickAnag(r)} data-testid={`${testId}-result`} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-800/60 border-b border-slate-800 last:border-0">
                      <span className="text-slate-100 font-medium">{formatNominativo(r) || "—"}</span>
                      {r.codice_fiscale && <span className="text-slate-500 font-mono ml-2 text-xs">{r.codice_fiscale}</span>}
                      {r.data_nascita && <span className="text-amber-400/80 font-mono ml-2 text-xs">{formatDate(r.data_nascita)}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {showRes && results.length === 0 && q.trim().length >= 2 && !creating && (
              <button type="button" onClick={() => { setCreating(true); setShowRes(false); onChange({ is_cliente: false }); }} data-testid={`${testId}-crea-nuovo`}
                className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-teal-500/40 bg-teal-500/10 px-3 py-1.5 text-sm font-medium text-teal-300 hover:bg-teal-500/20">
                <Plus className="h-4 w-4" /> Crea nuovo conducente
              </button>
            )}
          </div>

          {showForm && (
            <div className="rounded-md border border-teal-600/40 bg-teal-500/5 p-4 space-y-4" data-testid={`${testId}-form`}>
              <div className="grid sm:grid-cols-3 gap-3">
                <TF label="Cognome" value={c.cognome} onChange={(v) => set({ cognome: v })} testId={`${testId}-cognome`} />
                <TF label="Nome" value={c.nome} onChange={(v) => set({ nome: v })} testId={`${testId}-nome`} />
                <Combobox label="Sesso" value={c.sesso} onChange={(v) => set({ sesso: v })} options={SESSO} testId={`${testId}-sesso`} />
                <DateField label="Data di Nascita" value={c.data_nascita} onChange={(v) => set({ data_nascita: v })} testId={`${testId}-nascita`} />
                <Combobox label="Comune di Nascita" value={c.comune_nascita} onChange={(v) => set({ comune_nascita: v })} onSelectOption={(o) => set({ comune_nascita: o.v, provincia_nascita: o.prov || "" })} allowFree testId={`${testId}-comune-nascita`} loader={comuneLoader} />
                <div><Label className={LBL}>Provincia di Nascita</Label><Input value={c.provincia_nascita || ""} readOnly data-testid={`${testId}-prov-nascita`} className={`${INPUT_CLS} font-mono bg-slate-800/60`} placeholder="—" title="Si popola dal comune di nascita" /></div>
                <div className="sm:col-span-2">
                  <Label className={LBL}>Codice Fiscale</Label>
                  <div className="flex gap-2 mt-1.5">
                    <Input value={c.codice_fiscale || ""} onChange={(e) => set({ codice_fiscale: e.target.value.toUpperCase() })} data-testid={`${testId}-cf`} className="h-9 bg-slate-900/60 border-slate-700 font-mono" />
                    <Button type="button" onClick={generaCF} data-testid={`${testId}-genera-cf`} className="bg-violet-600 hover:bg-violet-700 text-white shrink-0"><Sparkles className="h-4 w-4 mr-1" /> Genera CF</Button>
                    <DecodificaCFButton cf={c.codice_fiscale} testId={`${testId}-decodifica-cf`} className="h-9 shrink-0" onDecode={(d) => set({ sesso: d.sesso === "F" ? "Femmina" : "Maschio", data_nascita: d.data_nascita, comune_nascita: d.comune_nascita, provincia_nascita: d.provincia_nascita })} />
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-4 gap-3">
                <TF label="Patente N" value={c.patente_n} onChange={(v) => set({ patente_n: v })} testId={`${testId}-patente-n`} mono />
                <TF label="Categoria Patente" value={c.patente_categoria} onChange={(v) => set({ patente_categoria: v })} testId={`${testId}-patente-cat`} />
                <DateField label="Data Rilascio Patente" value={c.patente_rilascio} onChange={(v) => set({ patente_rilascio: v })} testId={`${testId}-patente-rilascio`} />
                <DateField label="Data Scadenza Patente" value={c.patente_scadenza} onChange={(v) => set({ patente_scadenza: v })} testId={`${testId}-patente-scadenza`} />
              </div>

              <div className="grid sm:grid-cols-4 gap-3">
                <div className="sm:col-span-2">
                  <Label className={LBL}>Indirizzo</Label>
                  <div className="mt-1.5"><AddressAutocomplete value={c.indirizzo} onChange={(v) => set({ indirizzo: v })} onPick={(p) => onChange({ ...c, indirizzo: p.indirizzo, comune: p.comune, cap: p.cap, provincia: p.provincia })} city={c.comune} prov={c.provincia} testId={`${testId}-indirizzo`} /></div>
                </div>
                <TF label="N° Civ." value={c.numero_civico} onChange={(v) => set({ numero_civico: v })} testId={`${testId}-civico`} />
                <div><Label className={LBL}>CAP</Label><Input value={c.cap || ""} onChange={(e) => set({ cap: e.target.value })} data-testid={`${testId}-cap`} className={`${INPUT_CLS} font-mono`} /></div>
                <Combobox label="Comune" value={c.comune} onChange={(v) => set({ comune: v })} onSelectOption={(o) => onChange({ ...c, comune: o.v, provincia: o.prov || "", cap: o.cap || c.cap })} allowFree testId={`${testId}-comune`} loader={comuneLoader} />
                <div><Label className={LBL}>Provincia</Label><Input value={c.provincia || ""} onChange={(e) => set({ provincia: e.target.value.toUpperCase() })} data-testid={`${testId}-provincia`} className={`${INPUT_CLS} font-mono`} /></div>
                <TF label="Cellulare" value={c.cellulare} onChange={(v) => set({ cellulare: v })} testId={`${testId}-cellulare`} wa extra={c.cellulari_extra || []} onExtra={(v) => set({ cellulari_extra: v })} labels={c.cellulari_etichette || []} onLabels={(v) => set({ cellulari_etichette: v })} />
                <TF label="Email" value={c.email} onChange={(v) => set({ email: v })} testId={`${testId}-email`} w2 />
              </div>

              <div className="flex items-center justify-between gap-3 flex-wrap">
                <label className="inline-flex items-center gap-2 text-sm text-slate-200 cursor-pointer" data-testid={`${testId}-come-cliente-wrap`}>
                  <Switch checked={!!c.come_cliente} onCheckedChange={(v) => set({ come_cliente: !!v })} data-testid={`${testId}-come-cliente`} />
                  Attiva come secondo cliente <span className="text-[11px] text-slate-500">(compare nella lista clienti con badge CONDUCENTE)</span>
                </label>
                <Button type="button" onClick={reset} data-testid={`${testId}-reset`} className="border border-slate-700 bg-slate-800 text-white hover:bg-slate-700">Rimuovi conducente</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Conducente nuovo inserito nel wizard: lo salva in Anagrafica Clienti e ne memorizza il cliente_id.
async function linkCondAnag(c) {
  if (!c || c.is_cliente !== false || c.cliente_id || !(c.cognome || c.nome)) return c;
  const { is_cliente, come_cliente, pro_salvati, nominativo, ...rest } = c;
  const payload = { ...rest, soggetto_iva: "NO", ragione_sociale: [c.cognome, c.nome].filter(Boolean).join(" "),
    luogo_nascita: c.comune_nascita || "", provincia_nascita: c.provincia_nascita || "", comune_nascita_prov: c.provincia_nascita || "" };
  const { record } = await guardCreate("clienti", payload);
  return record?.id ? { ...c, cliente_id: record.id } : c;
}

function buildScheda(r) {
  const v0 = (r && r.veicoli && r.veicoli[0]) || {};
  const { _id, id, type, tenant_id, created_at, cestino, ...rest } = r || {};
  return {
    ...rest,
    cognome: r.cognome || "", nome: r.nome || "", ragione_sociale: r.ragione_sociale || r.denominazione || formatNominativo(r) || "",
    codice_fiscale: r.codice_fiscale || "", partita_iva: r.partita_iva || "",
    indirizzo: r.indirizzo || "", numero_civico: r.numero_civico || "", cap: r.cap || "", comune: r.comune || "", provincia: r.provincia || "",
    cellulare: r.cellulare || r.cell || "", telefono: r.telefono || r.tel_casa || "", tel_ufficio: r.tel_ufficio || "", fax: r.fax || "",
    email: r.email || "", pec: r.pec || "", codice_destinatario: r.codice_destinatario || "", iban: r.iban || "",
    sesso: r.sesso || "", data_nascita: r.data_nascita || "", luogo_nascita: r.luogo_nascita || r.comune_nascita || "", provincia_nascita: r.provincia_nascita || r.comune_nascita_prov || "",
    professione: r.professione || "", soggetto_iva: r.soggetto_iva || "", ...rapprOf(r),
    documento: r.documento || r.tipo_documento || "", numero_doc: r.numero_doc || r.numero_documento || "",
    rilasciato: r.rilasciato || r.documento_rilasciato_da || "", luogo_rilascio_doc: r.luogo_rilascio_doc || r.doc_luogo_rilascio || "",
    data_emissione_doc: r.data_emissione_doc || r.doc_data_rilascio || r.data_rilascio || "", data_scadenza_doc: r.data_scadenza_doc || r.doc_data_scadenza || "",
    ispettorato: r.ispettorato || "",
    targa: r.targa || v0.targa || "", numero_polizza: r.numero_polizza || r.polizza_n || v0.numero_polizza || "",
    compagnia: r.compagnia || r.assicurazione || v0.compagnia || "", assicurazione: r.compagnia || r.assicurazione || v0.compagnia || "",
    costruttore: r.costruttore || v0.marca || "", modello: r.modello || v0.modello || "", colore: r.colore || v0.colore || "", scadenza_polizza: r.scadenza_polizza || v0.scadenza_polizza || "",
    num_sinistro: r.num_sinistro || r.assistito_num_sinistro || "",
    veicoli: Array.isArray(r.veicoli) ? r.veicoli : (v0 && Object.keys(v0).length ? [v0] : []),
  };
}

// Snapshot completo del testimone: porta TUTTI i campi anagrafici inseriti nel wizard
// (contatti, residenza, documento, professione, nascita) nella pratica, normalizzando nominativo/cliente_id.
function buildTestimone(r) {
  const o = r || {};
  const { _id, id, type, tenant_id, created_at, cestino, veicoli, ...rest } = o;
  return {
    ...rest,
    nominativo: o.nominativo || formatNominativo(o) || [o.cognome, o.nome].filter(Boolean).join(" ").trim(),
    cognome: o.cognome || "", nome: o.nome || "", sesso: o.sesso || "",
    data_nascita: o.data_nascita || "",
    comune_nascita: o.comune_nascita || o.luogo_nascita || "",
    comune_nascita_prov: o.comune_nascita_prov || o.provincia_nascita || "",
    codice_fiscale: o.codice_fiscale || "",
    professione: o.professione || "",
    cellulare: o.cellulare || o.cell || "", telefono: o.telefono || o.tel_casa || "",
    indirizzo: o.indirizzo || "", comune: o.comune || "", cap: o.cap || "", provincia: o.provincia || "",
    tipo_documento: o.tipo_documento || o.documento || "", numero_documento: o.numero_documento || o.numero_doc || "",
    documento_rilasciato_da: o.documento_rilasciato_da || o.rilasciato || "",
    doc_luogo_rilascio: o.doc_luogo_rilascio || o.luogo_rilascio_doc || "",
    doc_data_rilascio: o.doc_data_rilascio || o.data_emissione_doc || "",
    doc_data_scadenza: o.doc_data_scadenza || o.data_scadenza_doc || "",
    cliente_id: o.cliente_id || id || null,
  };
}

// ---- Step generico con ricerca predittiva + tabella + form inline ----
function EntityStep({ kind, searchLabel, rows, setRows, columns, dataSinistro, tipoPratica, testId, picked: pickedProp, setPicked: setPickedProp }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [formState, setFormState] = useState(null);
  const [showRes, setShowRes] = useState(false);
  const [pickedInner, setPickedInner] = useState([]);
  const picked = pickedProp || pickedInner;
  const setPicked = setPickedProp || setPickedInner;
  const timer = useRef(null);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setResults([]); setShowRes(false); return; }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/anagrafiche/${KIND_ATYPE[kind] || "clienti"}?q=${encodeURIComponent(q)}`);
        setResults(data || []); setShowRes(true);
      } catch { setResults([]); }
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  const patch = (i, k, v) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [k]: v } : row)));
  const del = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const addOrReplaceRow = (row) => setRows((rs) => {
    const cf = (row.codice_fiscale || "").trim().toUpperCase();
    const idx = rs.findIndex((x) =>
      (row.cliente_id && x.cliente_id && x.cliente_id === row.cliente_id) ||
      (cf && (x.codice_fiscale || "").trim().toUpperCase() === cf));
    if (idx >= 0) { const copy = [...rs]; copy[idx] = { ...rs[idx], ...row }; return copy; }
    return [...rs, row];
  });

  const pick = (r) => {
    const nome = formatNominativo(r);
    const v0 = (r.veicoli && r.veicoli[0]) || {};
    if (kind === "testimone") {
      addOrReplaceRow(buildTestimone({ ...r, nominativo: nome, cliente_id: r.id }));
    } else {
      addOrReplaceRow({ ragione_sociale: nome, targa: r.targa || v0.targa || "", polizza_n: r.numero_polizza || r.polizza_n || v0.numero_polizza || "", codice_fiscale: r.codice_fiscale || "", cliente_id: r.id, ruolo: r.ruolo || "", compagnia: r.compagnia || r.assicurazione || v0.compagnia || "", scheda: buildScheda(r), ...(kind === "controparte" ? { veicolo: r.veicolo || (Object.keys(v0).length ? v0 : null) } : {}) });
      const entry = { nominativo: nome, data_nascita: r.data_nascita || "", codice_fiscale: r.codice_fiscale || "", cliente_id: r.id };
      setPicked((p) => {
        const cf = (entry.codice_fiscale || "").trim().toUpperCase();
        const dup = p.some((x) => (entry.cliente_id && x.cliente_id === entry.cliente_id) || (cf && (x.codice_fiscale || "").trim().toUpperCase() === cf));
        return dup ? p : [...p, entry];
      });
    }
    setFormState({ mode: "edit", record: r });
    setQ(""); setResults([]); setShowRes(false);
  };

  const keyOf = (x) => (x.cliente_id ? `id:${x.cliente_id}` : `cf:${(x.codice_fiscale || "").trim().toUpperCase()}`);
  const movePicked = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= picked.length) return;
    const np = [...picked];
    [np[i], np[j]] = [np[j], np[i]];
    setPicked(np);
    const order = np.map(keyOf);
    const rank = (r) => { const idx = order.indexOf(keyOf(r)); return idx === -1 ? 999 : idx; };
    setRows((rs) => [...rs].sort((a, b) => rank(a) - rank(b)));
  };

  const removePicked = (entry) => {
    const cf = (entry.codice_fiscale || "").trim().toUpperCase();
    const match = (x) => (entry.cliente_id && x.cliente_id === entry.cliente_id) || (cf && (x.codice_fiscale || "").trim().toUpperCase() === cf);
    setPicked((p) => p.filter((x) => !match(x)));
    setRows((rs) => rs.filter((x) => !match(x)));
    setFormState((s) => {
      if (!s || !s.record) return s;
      const rec = s.record;
      const recCf = (rec.codice_fiscale || "").trim().toUpperCase();
      const same = (entry.cliente_id && rec.id === entry.cliente_id) || (cf && recCf === cf);
      return same ? null : s;
    });
  };

  const onSavedNew = (rec) => {
    if (kind === "testimone") addOrReplaceRow(buildTestimone({ ...rec, cliente_id: rec.id || null }));
    else {
      const vc = rec.veicolo || null;
      const vp = !vc && rec.veicolo_pratica ? rec.veicolo_pratica : null;
      const sch = { ...buildScheda(rec), ...(vc ? { targa: vc.targa || rec.targa || "", numero_polizza: vc.numero_polizza || "", compagnia: vc.compagnia || rec.compagnia || "", assicurazione: vc.compagnia || rec.compagnia || "" } : {}) };
      if (vp) {
        Object.assign(sch, {
          targa: (vp.targa || "").toUpperCase(), costruttore: vp.marca || "", modello: vp.modello || "", colore: vp.colore || "",
          numero_polizza: vp.numero_polizza || "", scadenza_polizza: vp.scadenza_polizza || "", data_immatricolazione: vp.anno_immatricolazione || "",
          ...(vp.compagnia ? { compagnia: vp.compagnia, assicurazione: vp.compagnia } : {}),
          veicoli: [vp, ...((rec.veicoli || []).filter((x) => JSON.stringify(x) !== JSON.stringify(vp)))],
        });
      }
      delete sch.veicolo_pratica;
      addOrReplaceRow({ ragione_sociale: formatNominativo(rec), targa: (vc && vc.targa) || sch.targa || rec.targa || "", polizza_n: (vc && vc.numero_polizza) || sch.numero_polizza || rec.numero_polizza || rec.polizza_n || "", codice_fiscale: rec.codice_fiscale || "", cliente_id: rec.id || null, veicolo: vc, ruolo: rec.ruolo || (vc && vc.ruolo) || "", compagnia: (vc && vc.compagnia) || (vp && vp.compagnia) || rec.compagnia || rec.assicurazione || "", scheda: sch });
      const entry = { nominativo: formatNominativo(rec), data_nascita: rec.data_nascita || "", codice_fiscale: rec.codice_fiscale || "", cliente_id: rec.id || null };
      setPicked((p) => {
        const cf = (entry.codice_fiscale || "").trim().toUpperCase();
        const dup = p.some((x) => (entry.cliente_id && x.cliente_id === entry.cliente_id) || (cf && (x.codice_fiscale || "").trim().toUpperCase() === cf));
        return dup ? p : [...p, entry];
      });
    }
    setFormState(null);
  };

  // Ruolo del soggetto attualmente aperto nel form, letto dal menu della lista soggetti selezionati.
  const curSubjectRole = (() => {
    const rec = formState?.record;
    if (!rec) return "";
    const cf = (rec.codice_fiscale || "").trim().toUpperCase();
    const row = rows.find((x) => (rec.id && x.cliente_id === rec.id) || (cf && (x.codice_fiscale || "").trim().toUpperCase() === cf));
    return (row && row.ruolo) || rec.ruolo || "";
  })();

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-slate-700 bg-slate-900/40 p-3">
        <Label className={LBL}>{searchLabel}</Label>
        <div className="relative mt-1.5">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setShowRes(true)}
            placeholder="Digita cognome, ragione sociale o codice fiscale…" data-testid={`${testId}-search`} className="h-9 pl-8 bg-slate-900/60 border-slate-700" />
          {showRes && results.length > 0 && (
            <div className="absolute z-30 mt-1 w-full rounded-md border border-slate-700 bg-[#121824] shadow-lg max-h-64 overflow-y-auto" data-testid={`${testId}-results`}>
              {results.map((r) => (
                <button key={r.id} type="button" onClick={() => pick(r)} data-testid={`${testId}-result`} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-800/60 border-b border-slate-800 last:border-0">
                  <span className="text-slate-100 font-medium">{formatNominativo(r) || "—"}</span>
                  {r.codice_fiscale && <span className="text-slate-500 font-mono ml-2 text-xs">{r.codice_fiscale}</span>}
                  {r.data_nascita && <span className="text-amber-400/80 font-mono ml-2 text-xs">{formatDate(r.data_nascita)}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
        {showRes && results.length === 0 && q.trim().length >= 2 && formState?.mode !== "create" && (
          <button type="button" onClick={() => { setFormState({ mode: "create" }); setShowRes(false); }} data-testid={`${testId}-crea-nuovo`}
            className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-teal-500/40 bg-teal-500/10 px-3 py-1.5 text-sm font-medium text-teal-300 hover:bg-teal-500/20">
            <Plus className="h-4 w-4" /> Crea {kind === "controparte" ? "nuova controparte" : kind === "testimone" ? "nuovo testimone" : "nuovo cliente"}
          </button>
        )}
      </div>

      {kind !== "testimone" && picked.length > 0 && (
        <div className="space-y-2" data-testid={`${testId}-selected`}>
          {picked.map((p, i) => {
            const cf = (p.codice_fiscale || "").trim().toUpperCase();
            const row = rows.find((r) => (p.cliente_id && r.cliente_id === p.cliente_id) || (cf && (r.codice_fiscale || "").trim().toUpperCase() === cf));
            const missingCf = !(p.codice_fiscale && p.codice_fiscale.trim());
            const missingTarga = kind === "cliente" && !(row && row.targa && String(row.targa).trim());
            const missingRuolo = !(row && row.ruolo && String(row.ruolo).trim());
            const setRuolo = (v) => setRows((rs) => rs.map((x) => ((p.cliente_id && x.cliente_id === p.cliente_id) || (cf && (x.codice_fiscale || "").trim().toUpperCase() === cf)) ? { ...x, ruolo: v } : x));
            return (
              <div key={p.cliente_id || i} data-testid={`${testId}-selected-${i}`}
                className="rounded-md border border-teal-600/40 bg-teal-500/5 px-3 py-2">
                <div className="flex items-center gap-3 text-sm">
                  <div className="flex flex-col shrink-0">
                    <button type="button" onClick={() => movePicked(i, -1)} disabled={i === 0} data-testid={`${testId}-selected-up-${i}`}
                      title="Sposta su" className="text-slate-400 hover:text-teal-300 disabled:opacity-30 disabled:cursor-not-allowed leading-none"><ChevronUp className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => movePicked(i, 1)} disabled={i === picked.length - 1} data-testid={`${testId}-selected-down-${i}`}
                      title="Sposta giù" className="text-slate-400 hover:text-teal-300 disabled:opacity-30 disabled:cursor-not-allowed leading-none"><ChevronDown className="h-3.5 w-3.5" /></button>
                  </div>
                  <span className="text-slate-900 font-medium">{p.nominativo || "—"}</span>
                  {kind === "cliente" && row && row.targa ? (
                    <span className="rounded border border-amber-500/60 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-amber-700" title="Veicolo usato per questa pratica" data-testid={`${testId}-selected-targa-${i}`}>{String(row.targa).toUpperCase()}</span>
                  ) : null}
                  {i === 0 && kind === "cliente" && (
                    <span className="rounded bg-teal-600 text-white text-[10px] font-semibold uppercase px-1.5 py-0.5" data-testid={`${testId}-principale-badge`}>Principale</span>
                  )}
                  <span className="text-slate-400">|</span>
                  <span className="text-amber-700 font-mono font-semibold">{p.data_nascita ? formatDate(p.data_nascita) : "—"}</span>
                  <span className="text-slate-400">|</span>
                  <span className="text-slate-700 font-mono">{p.codice_fiscale || "—"}</span>
                  <button type="button" onClick={() => removePicked(p)} data-testid={`${testId}-selected-del-${i}`}
                    title="Rimuovi cliente selezionato" className="ml-auto text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Label className={`${LBL} shrink-0`}>Ruolo Sinistro <span className="text-red-400">*</span></Label>
                  <select value={(row && row.ruolo) || ""} onChange={(e) => setRuolo(e.target.value)} data-testid={`${testId}-selected-ruolo-${i}`}
                    className={`h-9 rounded-md bg-slate-900/60 border text-slate-100 text-sm px-2 cursor-pointer focus:outline-none focus:ring-1 focus:ring-amber-500/40 ${missingRuolo ? "border-red-500/60" : "border-slate-700"}`}>
                    <option value="" className="bg-[#121824]">— seleziona ruolo —</option>
                    {RUOLI.map((rl) => <option key={rl} value={rl} className="bg-[#121824]">{rl}</option>)}
                  </select>
                </div>
                {(missingCf || missingTarga || missingRuolo) && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-[11px] text-amber-400" data-testid={`${testId}-selected-warning-${i}`}>
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                    <span>{missingRuolo ? "Ruolo Sinistro obbligatorio. " : ""}{(missingCf || missingTarga) ? `Attenzione: ${[missingCf ? "codice fiscale" : null, missingTarga ? "targa" : null].filter(Boolean).join(" e ")} mancante — puoi comunque proseguire.` : ""}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {kind === "testimone" && (
      <div className="rounded-md border border-slate-700 overflow-hidden">
        <table className="w-full text-sm" data-testid={`${testId}-table`}>
          <thead>
            <tr className="text-left text-slate-400 text-[10px] uppercase tracking-wider font-mono bg-slate-800/60">
              <th className="px-3 py-2 w-10">
                <button type="button" onClick={() => setFormState((s) => (s && s.mode === "new" ? null : { mode: "new", record: null }))} data-testid={`${testId}-new`} title="Aggiungi nuovo" className={`h-6 w-6 rounded flex items-center justify-center ${formState && formState.mode === "new" ? "bg-amber-500 text-[#0a0d14]" : "bg-teal-500/20 text-teal-300 hover:bg-teal-500/30"}`}><Plus className="h-4 w-4" /></button>
              </th>
              {columns.map((c) => <th key={c.key} className="px-3 py-2">{c.label}</th>)}
              <th className="px-3 py-2 w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columns.length + 2} className="px-3 py-6 text-center text-slate-500 text-sm">Nessuna voce. Cerca sopra o usa "+" per aggiungere.</td></tr>
            ) : rows.map((row, i) => (
              <tr key={i} className="border-t border-slate-800" data-testid={`${testId}-row-${i}`}>
                <td className="px-3 py-1.5 text-slate-500 font-mono text-center">{i + 1}</td>
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-1.5">
                    <Input value={row[c.key] || ""} onChange={(e) => patch(i, c.key, e.target.value)} data-testid={`${testId}-${i}-${c.key}`} className={`h-8 bg-slate-900/60 border-slate-700 ${c.mono ? "font-mono" : ""}`} />
                  </td>
                ))}
                <td className="px-3 py-1.5 text-center whitespace-nowrap">
                  <button type="button" onClick={() => setFormState({ mode: "row", index: i, record: row })} data-testid={`${testId}-edit-${i}`} title="Visualizza / modifica testimone" className="mr-2 text-slate-400 hover:text-amber-500"><Pencil className="h-4 w-4" /></button>
                  <button type="button" onClick={() => del(i)} data-testid={`${testId}-del-${i}`} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {formState && formState.mode === "create" && (
        <NewSubjectForm kind={kind} initialName={q} tipoPratica={tipoPratica} onSaved={onSavedNew} onCancel={() => setFormState(null)} testId={`${testId}-crea`} />
      )}
      {formState && formState.mode !== "create" && (kind === "testimone"
        ? <NewSubjectForm kind="testimone" key={`${formState.mode}-${formState.index ?? ""}-${formState.record?.id || formState.record?.cliente_id || "new"}`} initial={formState.record || null}
            onSaved={(rec) => {
              if (formState.mode === "row") { const idx = formState.index; setRows((rs) => rs.map((x, j) => (j === idx ? buildTestimone({ ...x, ...rec, cliente_id: rec.id || x.cliente_id || null }) : x))); setFormState(null); }
              else onSavedNew(rec);
            }}
            onCancel={() => setFormState(null)} testId={`${testId}-nuovo`} />
        : <ClienteForm atype={KIND_ATYPE[kind] || "clienti"} key={formState.record?.id || "new"} initial={formState.record} dataSinistro={dataSinistro} tipoPratica={tipoPratica} subjectRole={curSubjectRole} onSaved={onSavedNew} onCancel={() => setFormState(null)} testId={`${testId}-nuovo`} />)}
    </div>
  );
}

const EMPTY = () => ({
  data_apertura: todayISO(), segnalatore: "", tipo: "",
  procedura_card: "", lesioni: "", data_sinistro: "", ora_sinistro: "",
  luogo_provincia: "", luogo_sinistro: "", indirizzo_sinistro: "",
  veicoli_coinvolti: 2, veicoli: [{ tipo: "", assicurato_ita: true }, { tipo: "", assicurato_ita: true }],
  barem_cliente: "", barem_controparte: "", autorita_intervenute: "",
  autorita_tipo: "", autorita_di: "", autorita_verbale_n: "", autorita_verbale_data: "",
  sinistro_lat: "", sinistro_lon: "",
});

export default function NuovaPraticaWizard({ open, onOpenChange, onCreated, onOpenCreated }) {
  const [step, setStep] = useState(1);
  const [maxStep, setMaxStep] = useState(1);
  const [tipiPairs, setTipiPairs] = useState(asPairs(DEFAULT_TIPOLOGIE_SINISTRO));
  const [tipiList, setTipiList] = useState(DEFAULT_TIPOLOGIE_SINISTRO);
  useEffect(() => { fetchTipologie().then((l) => { TIPI_LIST = l; setTipiList(l); setTipiPairs(asPairs(l)); }); }, []);
  const [form, setForm] = useState(EMPTY());
  const [clienti, setClienti] = useState([]);
  const [controparti, setControparti] = useState([]);
  const nonAderente = useNonAderenti();
  const cpNA = nonAderente(controparti[0]?.compagnia || controparti[0]?.scheda?.compagnia || "");
  const [testimoni, setTestimoni] = useState([]);
  const [pickedClienti, setPickedClienti] = useState([]);
  const [pickedControparti, setPickedControparti] = useState([]);
  const [conducente, setConducente] = useState({ is_cliente: true });
  const [cpConducente, setCpConducente] = useState({ is_cliente: true });
  const [saving, setSaving] = useState(false);
  const [createdNumero, setCreatedNumero] = useState("");
  const [createdData, setCreatedData] = useState(null);
  const [reqFields, setReqFields] = useState([]);
  const [veicoliErr, setVeicoliErr] = useState(false);
  const [segnOpen, setSegnOpen] = useState(false);
  const [segnRec, setSegnRec] = useState(null);
  const descHtml = useRef("");
  const [missOpen, setMissOpen] = useState(false);
  const [veicoloWalk, setVeicoloWalk] = useState(false);
  const footerRef = useRef(null);

  const DRAFT_KEY = "pgp_wizard_draft";
  const draftRestored = useRef(false);
  useEffect(() => {
    if (!open) { draftRestored.current = false; return; }
    let draft = null;
    try { const raw = localStorage.getItem(DRAFT_KEY); if (raw) draft = JSON.parse(raw); } catch { /* bozza illeggibile */ }
    if (draft && draft.form) {
      setStep(draft.step || 1); setMaxStep(draft.maxStep || draft.step || 1);
      setForm({ ...EMPTY(), ...draft.form });
      setClienti(draft.clienti || []); setControparti(draft.controparti || []); setTestimoni(draft.testimoni || []);
      setPickedClienti(draft.pickedClienti || []); setPickedControparti(draft.pickedControparti || []);
      setConducente(draft.conducente || { is_cliente: true }); setCpConducente(draft.cpConducente || { is_cliente: true });
      setCreatedNumero(""); descHtml.current = draft.descHtml || "";
      toast.info("Bozza pratica ripristinata");
    } else {
      setStep(0); setMaxStep(0); setForm(EMPTY()); setClienti([]); setControparti([]); setTestimoni([]); setPickedClienti([]); setPickedControparti([]); setConducente({ is_cliente: true }); setCpConducente({ is_cliente: true }); setCreatedNumero(""); descHtml.current = "";
    }
    draftRestored.current = true;
    api.get("/studio/me").then(({ data }) => { const rf = data?.settings?.required_fields; setReqFields(rf !== undefined && rf !== null ? rf : DEFAULT_REQUIRED); }).catch(() => setReqFields(DEFAULT_REQUIRED));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autosalvataggio bozza: persiste lo stato del wizard in localStorage finché non si conferma o si annulla.
  useEffect(() => {
    if (!open || !draftRestored.current || createdNumero) return;
    const t = setTimeout(() => {
      try {
        const hasData = JSON.stringify(form) !== JSON.stringify(EMPTY()) || clienti.length || controparti.length || testimoni.length || (descHtml.current || "").trim();
        if (!hasData) { localStorage.removeItem(DRAFT_KEY); return; }
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ step, maxStep, form, clienti, controparti, testimoni, pickedClienti, pickedControparti, conducente, cpConducente, descHtml: descHtml.current || "" }));
      } catch { /* quota superata: ignora */ }
    }, 400);
    return () => clearTimeout(t);
  }, [open, createdNumero, step, maxStep, form, clienti, controparti, testimoni, pickedClienti, pickedControparti, conducente, cpConducente]);

  // Sezione "Conducente del veicolo": default deselezionato quando visibile (ruolo con veicolo,
  // conducente diverso dal soggetto); reset a "coincide" quando nascosta.
  const clienteRuolo0 = clienti[0]?.ruolo || "";
  const controparteRuolo0 = controparti[0]?.ruolo || "";
  useEffect(() => {
    const show = conducenteSectionVisibile(clienteRuolo0);
    setConducente((c) => {
      if (!show) return { is_cliente: true };
      if (c && (c.is_cliente === false || c.cognome || c.nome || c.codice_fiscale || c.cliente_id)) return c;
      return { is_cliente: true };
    });
  }, [clienteRuolo0]);
  useEffect(() => {
    const show = conducenteSectionVisibile(controparteRuolo0);
    setCpConducente((c) => {
      if (!show) return { is_cliente: true };
      if (c && (c.is_cliente === false || c.cognome || c.nome || c.codice_fiscale || c.cliente_id)) return c;
      return { is_cliente: true };
    });
  }, [controparteRuolo0]);

  const reqSet = useMemo(() => new Set(reqFields), [reqFields]);
  const RL = (k, t) => (reqSet.has(k) ? `${t} *` : t);
  const missingKeysForStep = (s) => {
    const miss = [];
    REQUIREABLE_FIELDS.filter((rf) => rf.step === s && reqSet.has(rf.key)).forEach((rf) => {
      const k = rf.key;
      let ok;
      if (k === "cliente_presente") ok = clienti.length > 0;
      else if (k === "cliente_cf") ok = !!(clienti[0] && clienti[0].codice_fiscale);
      else if (k === "cliente_targa") ok = !!(clienti[0] && clienti[0].targa);
      else if (k === "controparte_presente") ok = controparti.length > 0;
      else if (k === "testimone_presente") ok = testimoni.length > 0;
      else if (k === "veicoli_coinvolti") ok = !veicoloVisibile(form.tipo) || (form.veicoli || []).every((v) => v && v.tipo && String(v.tipo).trim());
      else ok = !!(form[k] !== undefined && form[k] !== null && String(form[k]).trim());
      if (!ok) miss.push(k);
    });
    return miss;
  };
  const labelFor = (k) => (REQUIREABLE_FIELDS.find((r) => r.key === k) || {}).label || k;
  const missingForStep = (s) => missingKeysForStep(s).map(labelFor);

  // Mappa campo obbligatorio -> data-testid dell'elemento nel wizard (per scroll/focus).
  const KEY_TESTID = {
    data_apertura: "w-data-apertura", segnalatore: "w-segnalatore", tipo: "w-tipo",
    procedura_card: "w-procedura", lesioni: "w-lesioni", data_sinistro: "w-data-sinistro",
    ora_sinistro: "w-ora", luogo_sinistro: "w-citta", luogo_provincia: "w-provincia",
    indirizzo_sinistro: "w-indirizzo", veicoli_coinvolti: "w-n-veicoli",
    barem_cliente: "w-barem-cliente", barem_controparte: "w-barem-controparte",
    autorita_intervenute: "w-autorita",
    cliente_presente: "w-clienti-search", cliente_cf: "w-clienti-search", cliente_targa: "w-clienti-search",
    controparte_presente: "w-controparti-search", testimone_presente: "w-testimoni-search",
  };
  const focusByTestId = (tid) => {
    if (!tid) return;
    const el = document.querySelector(`[data-testid="${tid}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("pgp-miss-flash");
    setTimeout(() => el.classList.remove("pgp-miss-flash"), 2600);
    if (typeof el.focus === "function") setTimeout(() => { try { el.focus({ preventScroll: true }); } catch { el.focus(); } }, 320);
  };
  const focusField = (key) => {
    if (key === "veicoli_coinvolti") {
      const empIdx = (form.veicoli || []).findIndex((v) => !(v && v.tipo && String(v.tipo).trim()));
      return focusByTestId(empIdx >= 0 ? `w-veicolo-${empIdx}-tipo` : KEY_TESTID.veicoli_coinvolti);
    }
    return focusByTestId(KEY_TESTID[key]);
  };
  // Avanzamento progressivo: quando un campo mancante viene compilato, porta al successivo;
  // quando non ne restano, chiude il riepilogo e riporta al pulsante Avanti.
  const prevMissRef = useRef(0);
  useEffect(() => {
    if (!missOpen) { prevMissRef.current = 0; return; }
    const miss = missingKeysForStep(step);
    if (miss.length === 0) {
      setMissOpen(false);
      prevMissRef.current = 0;
      setTimeout(() => footerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 90);
      return;
    }
    if (prevMissRef.current && miss.length < prevMissRef.current) focusField(miss[0]);
    prevMissRef.current = miss.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missOpen, step, clienti, controparti, testimoni, form, reqFields]);
  useEffect(() => { setMissOpen(false); setVeicoloWalk(false); }, [step]);

  // Avanzamento progressivo tra i select "Veicolo N": attivato cliccando il campo mancante nel footer.
  const prevVeicEmptyRef = useRef(null);
  useEffect(() => {
    if (!veicoloWalk) { prevVeicEmptyRef.current = null; return; }
    if (!veicoloVisibile(form.tipo)) { setVeicoloWalk(false); return; }
    const empIdx = (form.veicoli || []).findIndex((v) => !(v && v.tipo && String(v.tipo).trim()));
    if (empIdx < 0) {
      setVeicoloWalk(false);
      prevVeicEmptyRef.current = null;
      setTimeout(() => footerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 90);
      return;
    }
    if (prevVeicEmptyRef.current !== null && empIdx !== prevVeicEmptyRef.current) focusByTestId(`w-veicolo-${empIdx}-tipo`);
    prevVeicEmptyRef.current = empIdx;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [veicoloWalk, form.veicoli, form.tipo]);

  useEffect(() => { setMaxStep((m) => Math.max(m, step)); }, [step]);
  const gotoStep = (n) => { if (createdNumero) return; if (n <= maxStep) { setMissOpen(false); setStep(n); } };

  const goNext = () => {
    if (step === 0) {
      if (!form.tipo) { toast.error("Seleziona il tipo di pratica"); return; }
      setStep(1); return;
    }
    if (step === 1 && veicoloVisibile(form.tipo)) {
      const badVeicoli = (form.veicoli || []).some((v) => !(v && v.tipo && String(v.tipo).trim()));
      setVeicoliErr(badVeicoli);
      if (badVeicoli) { toast.error("Selezionare la tipologia del veicolo per ogni veicolo"); return; }
    }
    if (step === 2) {
      // Step "Dati Clienti": avanza appena almeno un cliente è nella lista di selezione,
      // indipendentemente dal campo di ricerca o da altri campi (CF/targa restano
      // eventualmente validati alla conferma finale secondo la config del titolare).
      if (clienti.length === 0) { toast.error("Seleziona almeno un cliente"); return; }
      if (clienti.some((c) => !(c.ruolo && String(c.ruolo).trim()))) { toast.error("Seleziona il Ruolo Sinistro per ogni cliente"); return; }
      setStep((s) => s + 1);
      return;
    }
    if (step === 3 && controparti.some((c) => !(c.ruolo && String(c.ruolo).trim()))) {
      toast.error("Seleziona il Ruolo Sinistro per ogni controparte"); return;
    }
    const missKeys = missingKeysForStep(step);
    if (missKeys.length) { setMissOpen(true); setTimeout(() => focusField(missKeys[0]), 90); return; }
    setMissOpen(false);
    setStep((s) => s + 1);
  };
  const confermaGuard = () => {
    const miss = [1, 2, 3, 4].flatMap((s) => missingForStep(s));
    if (miss.length) { toast.error(`Campi obbligatori mancanti: ${miss.join(", ")}`); return; }
    if (clienti.some((c) => !(c.ruolo && String(c.ruolo).trim()))) { toast.error("Seleziona il Ruolo Sinistro per ogni cliente"); return; }
    if (controparti.some((c) => !(c.ruolo && String(c.ruolo).trim()))) { toast.error("Seleziona il Ruolo Sinistro per ogni controparte"); return; }
    salva();
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const curMissingSet = new Set(missingKeysForStep(step));
  // Allo step 2 il pulsante "Avanti" dipende SOLO dalla presenza di un cliente selezionato.
  const stepPresenceBlocked = step === 2 ? clienti.length === 0 : curMissingSet.size > 0;
  const ruoloBlocked = (step === 2 && clienti.some((c) => !(c.ruolo && String(c.ruolo).trim())))
    || (step === 3 && controparti.some((c) => !(c.ruolo && String(c.ruolo).trim())));
  const stepBlocked = (step === 0 && !form.tipo) || stepPresenceBlocked || ruoloBlocked || (step === 1 && veicoloVisibile(form.tipo) && (form.veicoli || []).some((v) => !(v && v.tipo && String(v.tipo).trim())));
  const Err = ({ k }) => (reqSet.has(k) && curMissingSet.has(k) ? <p className="text-[11px] text-red-400 mt-1" data-testid={`w-err-${k}`}>Campo obbligatorio</p> : null);
  const setVeicoli = (n) => {
    const num = Math.max(0, Math.min(20, parseInt(n, 10) || 0));
    setForm((f) => { const arr = [...(f.veicoli || [])]; while (arr.length < num) arr.push({ tipo: "", assicurato_ita: true }); arr.length = num; return { ...f, veicoli_coinvolti: num, veicoli: arr }; });
  };
  const patchVeicolo = (i, patch) => setForm((f) => ({ ...f, veicoli: f.veicoli.map((v, idx) => (idx === i ? { ...v, ...patch } : v)) }));
  const mapsUrl = () => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([form.indirizzo_sinistro, form.luogo_sinistro, form.luogo_provincia].filter(Boolean).join(", "))}`;
  const geocode = async (q) => {
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&countrycodes=it&accept-language=it&q=${encodeURIComponent(q)}`);
      const d = await r.json();
      if (Array.isArray(d) && d[0]) setForm((f) => ({ ...f, sinistro_lat: d[0].lat, sinistro_lon: d[0].lon }));
    } catch { /* ignore */ }
  };

  const salva = async () => {
    setSaving(true);
    let cond = conducente, cpCond = cpConducente;
    try {
      cond = await linkCondAnag(conducente);
      cpCond = await linkCondAnag(cpConducente);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSaving(false); return; }
    if (cond !== conducente) setConducente(cond);
    if (cpCond !== cpConducente) setCpConducente(cpCond);
    const conducente_ = cond, cpConducente_ = cpCond;
    const plainDesc = (descHtml.current || "").replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
    const first = clienti[0];
    const nameOf = (c) => (c && (c.ragione_sociale || [c.scheda && c.scheda.cognome, c.scheda && c.scheda.nome].filter(Boolean).join(" ").trim())) || "";
    const entryOf = (c) => ({ ...(c.scheda || {}), ragione_sociale: nameOf(c), cliente_id: c.cliente_id || null, ruolo: c.ruolo || "", targa: (c.scheda && c.scheda.targa) || c.targa || "", compagnia: (c.scheda && c.scheda.compagnia) || c.compagnia || "", assicurazione: (c.scheda && c.scheda.compagnia) || c.compagnia || "" });
    const cliEntries = clienti.map(entryOf);
    const firstE = cliEntries[0] || {};
    const firstComp = firstE.compagnia || firstE.assicurazione || "";
    const payload = {
      ...form,
      veicoli_coinvolti: Number(form.veicoli_coinvolti) || 0,
      data_sinistro: form.data_sinistro || null,
      data_apertura: form.data_apertura || null,
      descrizione_sinistro: plainDesc, descrizione_sinistro_html: descHtml.current || "",
      nome_assistito: firstE.ragione_sociale || "",
      assistito_ragione_sociale: firstE.ragione_sociale || "",
      assistito_ruolo: firstE.ruolo || "",
      assistito_compagnia: firstComp,
      compagnia: firstComp,
      targa: firstE.targa || "",
      codice_fiscale: firstE.codice_fiscale || "",
      assistito_num_sinistro: firstE.num_sinistro || "",
      cliente_id: null,
      assistito_cliente_id: (first && first.cliente_id) || null,
      assistito_scheda: { ...firstE, segnalatore: (form.segnalatore || "").trim() },
      assistiti: [
        { assicurazione: firstComp, compagnia: firstComp, targa: firstE.targa || "" },
        ...cliEntries.slice(1).map((e) => ({ ...e, ruolo: e.ruolo || "Assistito" })),
        ...((conducente_ && conducente_.is_cliente === false && conducente_.come_cliente) ? (() => {
          const { is_cliente, come_cliente, ...rest } = conducente_;
          const nm = (conducente_.nominativo || [conducente_.cognome, conducente_.nome].filter(Boolean).join(" ")).trim();
          return nm ? [{ ...rest, ragione_sociale: nm, ruolo: "Conducente", da_conducente: true, nominativo_conducente: nm }] : [];
        })() : []),
      ],
      controparti: controparti.map((c) => ({ ...(c.scheda || {}), ragione_sociale: c.ragione_sociale || "", targa: (c.scheda && c.scheda.targa) || c.targa || "", polizza: (c.scheda && c.scheda.numero_polizza) || c.polizza_n || "", ruolo: c.ruolo || "", compagnia: (c.scheda && c.scheda.compagnia) || c.compagnia || "", assicurazione: (c.scheda && c.scheda.compagnia) || c.compagnia || "", cliente_id: c.cliente_id || null, veicolo: c.veicolo || null })),
      testimoni: testimoni.map((t) => buildTestimone(t)),
      conducente: (conducente_ && conducente_.is_cliente === false) ? { ...conducente_, is_cliente: false } : { is_cliente: true },
      controparte_conducente: (cpConducente_ && cpConducente_.is_cliente === false) ? { ...cpConducente_, is_cliente: false } : { is_cliente: true },
      barem_responsabilita: (computeBarem(form.barem_cliente, form.barem_controparte) || {}).text || "",
    };
    try {
      const { data } = await api.post("/studio/pratiche", payload);
      setCreatedNumero(data.numero_pratica || "");
      setCreatedData(data);
      try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      onCreated && onCreated(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const chiudi = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } onOpenChange(false); };
  const openSegnalatore = async () => {
    const nome = (form.segnalatore || "").trim();
    if (!nome) return;
    const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
    const sortTok = (s) => norm(s).split(" ").filter(Boolean).sort().join(" ");
    const target = norm(nome);
    const targetSorted = sortTok(nome);
    try {
      const { data } = await api.get(`/anagrafiche/segnalatori`);
      const list = data || [];
      const rec = list.find((r) => {
        const dn = norm(displayName(r));
        const nc = norm([r.nome, r.cognome].filter(Boolean).join(" "));
        const cn = norm([r.cognome, r.nome].filter(Boolean).join(" "));
        const den = norm(r.denominazione);
        if (dn === target || nc === target || cn === target || (den && den === target)) return true;
        // Confronto indipendente dall'ordine (gestisce cognomi composti come "Di Maria").
        const recSorted = sortTok([r.nome, r.cognome].filter(Boolean).join(" "));
        return recSorted && recSorted === targetSorted;
      });
      if (!rec) { toast.error("Scheda segnalatore non trovata in anagrafica"); return; }
      setSegnRec(rec); setSegnOpen(true);
    } catch { toast.error("Impossibile aprire la scheda segnalatore"); }
  };
  // Lista live dei campi obbligatori non ancora compilati nello step corrente (footer accanto ad "Avanti").
  const footerMissing = (() => {
    const items = [];
    if (step === 1 && veicoloVisibile(form.tipo)) {
      const empIdx = (form.veicoli || []).findIndex((v) => !(v && v.tipo && String(v.tipo).trim()));
      if (empIdx >= 0) {
        const lbl = reqSet.has("veicoli_coinvolti") ? labelFor("veicoli_coinvolti") : "Tipologia Veicolo";
        items.push({ id: "veicoli_tipo", label: lbl, tid: `w-veicolo-${empIdx}-tipo` });
      }
    }
    if (step === 2) {
      if (clienti.length === 0) items.push({ id: "cliente_presente", label: "Almeno un cliente", tid: "w-clienti-search" });
      else {
        const idx = clienti.findIndex((c) => !(c.ruolo && String(c.ruolo).trim()));
        if (idx >= 0) items.push({ id: "cli_ruolo", label: "Ruolo Sinistro", tid: `w-clienti-selected-ruolo-${idx}` });
      }
    }
    if (step === 3) {
      const idx = controparti.findIndex((c) => !(c.ruolo && String(c.ruolo).trim()));
      if (idx >= 0) items.push({ id: "cp_ruolo", label: "Ruolo Sinistro", tid: `w-controparti-selected-ruolo-${idx}` });
    }
    missingKeysForStep(step).forEach((k) => {
      if (k === "cliente_presente" && items.some((i) => i.id === "cliente_presente")) return;
      if (k === "veicoli_coinvolti") return; // gestito sopra: focus sul primo select Veicolo non compilato
      items.push({ id: k, label: labelFor(k), tid: KEY_TESTID[k] });
    });
    return items;
  })();

  const btnDark = "border border-slate-700 bg-slate-800 text-white hover:bg-slate-700";
  const btnPrimary = "bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold";
  const SectionHead = ({ children }) => (<div className="rounded-md px-4 py-2.5 mb-4" style={{ background: "#0f1e3d", border: "1px solid #1e3a6b" }}><span className="text-sm font-head font-bold uppercase tracking-wide" style={{ color: "#ffffff" }}>{children}</span></div>);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-5xl w-[95vw] max-h-[85vh] overflow-y-auto p-6" data-testid="nuova-pratica-wizard">
        <div className="border-b px-6 py-4 -mx-6 -mt-6 mb-5" style={{ background: "#0f172a", borderColor: "#1e293b" }} data-testid="wizard-header">
          <h2 className="font-head text-xl font-extrabold tracking-wide" style={{ color: "#ffffff" }} data-testid="wizard-title">NUOVA PRATICA</h2>
          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            {STEPS.map((s, i) => {
              const n = i, active = n === step, done = n < step;
              const clickable = !createdNumero && n <= maxStep;
              return (
                <div key={s} className="flex items-center gap-1.5">
                  <button type="button" disabled={!clickable} onClick={() => gotoStep(n)} data-testid={`wizard-step-nav-${n}`}
                    style={active ? { background: "#f59e0b", color: "#0a0d14", borderColor: "#f59e0b" } : done ? { background: "#134e4a", color: "#5eead4", borderColor: "#0d9488" } : { background: "#1e293b", color: "#cbd5e1", borderColor: "#475569" }}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold tracking-wide transition-all ${clickable && !active ? "cursor-pointer hover:brightness-125" : active ? "cursor-default" : "cursor-not-allowed"}`}>
                    <span className="flex h-4 w-4 items-center justify-center rounded-full text-[10px]" style={active ? { background: "#0a0d14", color: "#fbbf24" } : done ? { background: "#0d9488", color: "#fff" } : { background: "#475569", color: "#f1f5f9" }}>{n + 1}</span>{s}
                  </button>
                  {n < STEPS.length - 1 && <ChevronRight className="h-3.5 w-3.5" style={{ color: "#94a3b8" }} />}
                </div>
              );
            })}
          </div>
        </div>

        {step === 0 && (
          <div className="space-y-4" data-testid="wizard-step-0">
            <SectionHead>SELEZIONA IL TIPO DI PRATICA</SectionHead>
            <p className="text-sm text-slate-400">I passi successivi mostreranno solo i campi pertinenti al tipo scelto.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {tipiList.filter((t) => !(cpNA && String(t.code).toUpperCase() === "RD")).map((t) => {
                const fl = tipoFlags(tipiList, t.code), sel = form.tipo === t.code;
                return (
                  <button key={t.code} type="button" onClick={() => { set("tipo", t.code); setStep(1); }} data-testid={`w-tipo-pick-${t.code}`}
                    className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${sel ? "border-amber-500 bg-amber-500/10" : "border-slate-700 hover:border-amber-500/60 hover:bg-slate-800/50"}`}>
                    <div className="font-mono text-sm font-bold text-amber-400">{t.code}</div>
                    <div className="text-xs text-slate-300 truncate">{t.label}</div>
                    <div className="text-[10px] text-slate-500 mt-1">{fl.soloProf ? "Solo professionisti" : fl.veicolo ? "Con dati veicolo" : "Senza dati veicolo"}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-5" data-testid="wizard-step-1">
            <SectionHead>DATI SINISTRO</SectionHead>
            <div className="grid sm:grid-cols-3 gap-4">
              <div><DateField label={RL("data_apertura", "Data di Apertura")} value={form.data_apertura} onChange={(v) => set("data_apertura", v)} testId="w-data-apertura" /><Err k="data_apertura" /></div>
              <div className="relative">
                <Combobox label={RL("segnalatore", "Segnalatore Pratica")} value={form.segnalatore} onChange={(v) => set("segnalatore", v)} fetchAtype="segnalatori" allowFree testId="w-segnalatore" />
                {form.segnalatore && form.segnalatore.trim() && (
                  <button type="button" onClick={openSegnalatore} data-testid="w-segnalatore-edit" title="Apri scheda segnalatore"
                    className="absolute right-0 top-0 flex items-center gap-1 text-[11px] font-semibold text-teal-300 hover:text-teal-200">
                    <Pencil className="h-3.5 w-3.5" /> Apri scheda
                  </button>
                )}
                <Err k="segnalatore" />
              </div>
              <div><Combobox label={RL("tipo", "Tipo Pratica")} value={form.tipo} onChange={(v) => set("tipo", v)} options={cpNA ? tipiPairs.filter((t) => String(t[0]).toUpperCase() !== "RD") : tipiPairs} testId="w-tipo" /><Err k="tipo" />{cpNA && <p className="mt-1 text-[11px] text-amber-400" data-testid="w-tipo-non-aderente">{NON_ADERENTE_MSG}</p>}</div>
              <div><Combobox label={RL("procedura_card", "Procedura Card / Doppia Firma")} value={form.procedura_card} onChange={(v) => set("procedura_card", v)} options={PROCEDURA_CARD} testId="w-procedura" /><Err k="procedura_card" /></div>
              <div><Combobox label={RL("lesioni", "Lesioni")} value={form.lesioni} onChange={(v) => set("lesioni", v)} options={LESIONI} testId="w-lesioni" /><Err k="lesioni" /></div>
              <div><DateField label={RL("data_sinistro", "Data del Sinistro")} value={form.data_sinistro} onChange={(v) => set("data_sinistro", v)} testId="w-data-sinistro" /><Err k="data_sinistro" /></div>
              <div><Label className={LBL}>{RL("ora_sinistro", "Ora")}</Label><Input type="time" value={form.ora_sinistro || ""} onChange={(e) => set("ora_sinistro", e.target.value)} data-testid="w-ora" className={INPUT_CLS} /><Err k="ora_sinistro" /></div>
            </div>

            <div className="rounded-md border border-slate-700 p-4">
              <div className="text-[11px] uppercase tracking-wider text-teal-300 font-mono mb-3">Luogo Sinistro</div>
              <div className="grid sm:grid-cols-[35%_80px_1fr_1.5rem] gap-4">
                <Combobox label={RL("luogo_sinistro", "Città")} value={form.luogo_sinistro} onChange={(v) => set("luogo_sinistro", v)} onSelectOption={(o) => { set("luogo_provincia", o.prov || ""); geocode([o.v, o.prov, "Italia"].filter(Boolean).join(", ")); }} allowFree testId="w-citta" placeholder="Digita il comune…"
                  loader={async (q) => { if (!q || q.length < 2) return []; return searchComuni(q, 30).map((c) => ({ v: c.nome, l: `${c.nome} (${c.provincia})`, prov: c.provincia, cap: c.cap || "" })); }} />
                <div>
                  <Label className={LBL}>{RL("luogo_provincia", "Provincia")}</Label>
                  <Input value={form.luogo_provincia || ""} readOnly data-testid="w-provincia" placeholder="—" className={`${INPUT_CLS} font-mono bg-slate-800/60 cursor-not-allowed`} title="La provincia si aggiorna dalla città selezionata" />
                  <Err k="luogo_provincia" />
                </div>
                <div>
                  <Label className={LBL}>{RL("indirizzo_sinistro", "Indirizzo")}</Label>
                  <div className="flex gap-2 mt-1.5">
                    <AddressAutocomplete value={form.indirizzo_sinistro} onChange={(v) => set("indirizzo_sinistro", v)} onPick={(p) => setForm((f) => ({ ...f, indirizzo_sinistro: p.indirizzo, luogo_sinistro: p.comune || f.luogo_sinistro, luogo_provincia: p.provincia || f.luogo_provincia, sinistro_lat: String(p.lat), sinistro_lon: String(p.lon) }))} city={form.luogo_sinistro} prov={form.luogo_provincia} testId="w-indirizzo" />
                    <a href={mapsUrl()} target="_blank" rel="noreferrer" data-testid="w-maps" title="Apri in Google Maps" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-700 bg-slate-800 text-amber-400 hover:bg-slate-700"><MapPin className="h-4 w-4" /></a>
                  </div>
                  <Err k="indirizzo_sinistro" />
                </div>
                <div className="hidden sm:block" aria-hidden="true" />
              </div>
              {form.sinistro_lat && form.sinistro_lon && (
                <div className="mt-4 rounded-md overflow-hidden border border-slate-700" data-testid="w-mappa">
                  <iframe title="Mappa Sinistro" className="w-full h-56 block" loading="lazy"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${(+form.sinistro_lon - 0.008)}%2C${(+form.sinistro_lat - 0.006)}%2C${(+form.sinistro_lon + 0.008)}%2C${(+form.sinistro_lat + 0.006)}&layer=mapnik&marker=${form.sinistro_lat}%2C${form.sinistro_lon}`} />
                </div>
              )}
            </div>

            {veicoloVisibile(form.tipo) && (
            <div className="rounded-md border border-slate-700 p-4">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-[11px] uppercase tracking-wider text-teal-300 font-mono">{RL("veicoli_coinvolti", "N° Veicoli Coinvolti nell'Urto")}</span>
                <Input type="number" min={0} value={form.veicoli_coinvolti} onChange={(e) => setVeicoli(e.target.value)} data-testid="w-n-veicoli" className="h-8 w-20 bg-slate-900/60 border-slate-700 font-mono" />
              </div>
              <div className="space-y-2 max-w-xl">
                {(form.veicoli || []).map((v, i) => {
                  const showErr = veicoliErr && !(v && v.tipo && String(v.tipo).trim());
                  return (
                    <div key={i} data-testid={`w-veicolo-${i}`}>
                      <div className="grid grid-cols-[80px_1fr_auto] items-center gap-3">
                        <span className="text-sm font-semibold text-slate-300">Veicolo {i + 1}</span>
                        <Combobox value={v.tipo} onChange={(val) => patchVeicolo(i, { tipo: val })} options={VEICOLO_TIPI} testId={`w-veicolo-${i}-tipo`} placeholder="Selezionare veicolo" />
                        <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer whitespace-nowrap">
                          <Checkbox checked={!!v.assicurato_ita} onCheckedChange={(c) => patchVeicolo(i, { assicurato_ita: !!c })} data-testid={`w-veicolo-${i}-ita`} /> Assicurato ITA
                        </label>
                      </div>
                      {showErr && <p className="text-[11px] text-red-400 mt-1 ml-[92px]" data-testid={`w-veicolo-${i}-error`}>Selezionare la tipologia del veicolo</p>}
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            {veicoloVisibile(form.tipo) && (
            <div className="rounded-md border border-slate-700 p-4">
              <div className="text-[11px] uppercase tracking-wider text-teal-300 font-mono mb-3">Barem Responsabilità</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Combobox label={RL("barem_cliente", "Veicolo Cliente")} value={form.barem_cliente} onChange={(v) => set("barem_cliente", v)} options={BAREM_CASI} testId="w-barem-cliente" placeholder="Seleziona caso Barem" /><Err k="barem_cliente" /></div>
                <div><Combobox label={RL("barem_controparte", "Veicolo Controparte")} value={form.barem_controparte} onChange={(v) => set("barem_controparte", v)} options={BAREM_CASI} testId="w-barem-controparte" placeholder="Seleziona caso Barem" /><Err k="barem_controparte" /></div>
              </div>
              {(() => {
                const b = computeBarem(form.barem_cliente, form.barem_controparte);
                if (!b) return null;
                return (
                  <div data-testid="w-barem-banner" className="mt-4 rounded-md px-4 py-3 text-center font-bold uppercase tracking-wide text-white" style={{ backgroundColor: b.bg }}>
                    {b.text}
                  </div>
                );
              })()}
            </div>
            )}

            <div>
              <Label className={LBL}>{RL("autorita_intervenute", "Autorità Intervenute")}</Label>
              <RadioGroup value={form.autorita_intervenute} onValueChange={(v) => setForm((f) => (v === "SI" ? { ...f, autorita_intervenute: v } : { ...f, autorita_intervenute: v, autorita_tipo: "", autorita_di: "", autorita_verbale_n: "", autorita_verbale_data: "" }))} className="flex gap-6 mt-2" data-testid="w-autorita">
                {["SI", "NO", "NON SO"].map((o) => (<label key={o} className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer"><RadioGroupItem value={o} data-testid={`w-autorita-${o.replace(/\s/g, "")}`} /> {o}</label>))}
              </RadioGroup>
              <Err k="autorita_intervenute" />
              {form.autorita_intervenute === "SI" && (
                <div className="grid sm:grid-cols-4 gap-3 mt-3" data-testid="w-autorita-dettagli">
                  <Combobox label="Tipo" value={form.autorita_tipo} onChange={(v) => set("autorita_tipo", v)} options={AUTORITA_TIPI} testId="w-autorita-tipo" />
                  <TF label="Di" value={form.autorita_di} onChange={(v) => set("autorita_di", v)} testId="w-autorita-di" placeholder="Es. Palermo" />
                  <TF label="N. Verbale" value={form.autorita_verbale_n} onChange={(v) => set("autorita_verbale_n", v)} testId="w-autorita-verbale-n" mono />
                  <DateField label="Data Verbale" value={form.autorita_verbale_data} onChange={(v) => set("autorita_verbale_data", v)} testId="w-autorita-verbale-data" />
                </div>
              )}
            </div>

            <div><Label className={LBL}>Descrizione Sinistro</Label><MiniRich htmlRef={descHtml} testId="w-descrizione" /></div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="wizard-step-2">
            <SectionHead>DATI CLIENTI</SectionHead>
            <EntityStep kind="cliente" searchLabel="CLIENTE" rows={clienti} setRows={setClienti} picked={pickedClienti} setPicked={setPickedClienti} dataSinistro={form.data_sinistro} tipoPratica={form.tipo} testId="w-clienti"
              columns={veicoloVisibile(form.tipo) ? [{ key: "ragione_sociale", label: "Ragione Sociale" }, { key: "targa", label: "Targa", mono: true }, { key: "polizza_n", label: "N Polizza", mono: true }] : [{ key: "ragione_sociale", label: "Ragione Sociale" }]} />
            {veicoloVisibile(form.tipo) && conducenteSectionVisibile(clienti[0]?.ruolo) && (
              <div className="mt-6">
                <ConducenteSection value={conducente} onChange={setConducente} subjectNome={clienti[0]?.ragione_sociale || ""} />
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div data-testid="wizard-step-3">
            <SectionHead>DATI CONTROPARTE</SectionHead>
            <EntityStep kind="controparte" searchLabel="CONTROPARTE" rows={controparti} setRows={setControparti} picked={pickedControparti} setPicked={setPickedControparti} dataSinistro={form.data_sinistro} tipoPratica={form.tipo} testId="w-controparti"
              columns={veicoloVisibile(form.tipo) ? [{ key: "ragione_sociale", label: "Ragione Sociale" }, { key: "targa", label: "Targa", mono: true }, { key: "polizza_n", label: "N° Polizza", mono: true }] : [{ key: "ragione_sociale", label: "Ragione Sociale" }]} />
            {veicoloVisibile(form.tipo) && conducenteSectionVisibile(controparti[0]?.ruolo) && (
              <div className="mt-6">
                <ConducenteSection value={cpConducente} onChange={setCpConducente} testId="w-cp-conducente"
                  checkboxLabel="La controparte è anche il conducente" subjectWord="la controparte"
                  subjectNome={controparti[0]?.ragione_sociale || ""}
                  hint="Se alla guida non c'era la controparte, indica qui i dati del conducente effettivo del veicolo controparte." />
              </div>
            )}
            {veicoloVisibile(form.tipo) && controparti.map((c, idx) => (
              <div key={c.cliente_id || idx} className="mt-6" data-testid={`w-cp-veicolo-sec-${idx}`}>
                <SectionHead>{`VEICOLO CONTROPARTE${controparti.length > 1 ? ` — ${c.ragione_sociale || idx + 1}` : ""}`}</SectionHead>
                <ControparteVeicolo veicolo={c.veicolo || null} testId={`w-cp-veicolo-${idx}`}
                  onChange={(v) => setControparti((cs) => cs.map((x, i) => (i !== idx ? x : {
                    ...x, veicolo: v, targa: v ? (v.targa || "") : x.targa, polizza_n: v ? (v.numero_polizza || "") : x.polizza_n,
                    compagnia: v && v.compagnia ? v.compagnia : x.compagnia,
                    scheda: { ...(x.scheda || {}), ...(v ? { targa: v.targa || "", numero_polizza: v.numero_polizza || "", costruttore: v.marca || "", modello: v.modello || "", colore: v.colore || "", scadenza_polizza: v.scadenza_polizza || "", num_sinistro: v.num_sinistro_compagnia || "", telaio: v.telaio || "", veicoli: [v], ...(/^\d{4}-\d{2}-\d{2}$/.test(v.anno_immatricolazione || "") ? { data_immatricolazione: v.anno_immatricolazione } : {}), ...(v.compagnia ? { compagnia: v.compagnia, assicurazione: v.compagnia } : {}) } : { targa: "", numero_polizza: "", costruttore: "", modello: "", colore: "", scadenza_polizza: "", num_sinistro: "", telaio: "", veicoli: [] }) },
                  })))} />
              </div>
            ))}
          </div>
        )}

        {step === 4 && (
          <div data-testid="wizard-step-4">
            <SectionHead>DATI TESTIMONE</SectionHead>
            <EntityStep kind="testimone" searchLabel="TESTIMONE" rows={testimoni} setRows={setTestimoni} testId="w-testimoni"
              columns={[{ key: "nominativo", label: "Cognome Nome" }, { key: "codice_fiscale", label: "Codice Fiscale", mono: true }]} />
          </div>
        )}

        {step === 5 && (
          <div data-testid="wizard-step-5">
            <SectionHead>CONFERMA</SectionHead>
            {!createdNumero ? (
              <div className="space-y-3 text-sm">
                <p className="text-slate-400">Verifica i dati e premi <b className="text-slate-200">Conferma e Salva</b>. Il numero pratica verrà assegnato automaticamente.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="rounded-md border border-slate-700 p-3"><span className="text-[11px] uppercase text-slate-500 font-mono">Tipo Pratica</span><div className="text-slate-100 mt-1">{form.tipo || "—"}</div></div>
                  <div className="rounded-md border border-slate-700 p-3"><span className="text-[11px] uppercase text-slate-500 font-mono">Data Sinistro</span><div className="text-slate-100 mt-1 font-mono">{form.data_sinistro ? formatDate(form.data_sinistro) : "—"}</div></div>
                  <div className="rounded-md border border-slate-700 p-3"><span className="text-[11px] uppercase text-slate-500 font-mono">Clienti</span><div className="text-slate-100 mt-1">{clienti.length}</div></div>
                  <div className="rounded-md border border-slate-700 p-3"><span className="text-[11px] uppercase text-slate-500 font-mono">Controparti</span><div className="text-slate-100 mt-1">{controparti.length}</div></div>
                  <div className="rounded-md border border-slate-700 p-3"><span className="text-[11px] uppercase text-slate-500 font-mono">Testimoni</span><div className="text-slate-100 mt-1">{testimoni.length}</div></div>
                  <div className="rounded-md border border-slate-700 p-3"><span className="text-[11px] uppercase text-slate-500 font-mono">Conducente</span><div className="text-slate-100 mt-1">{conducente && conducente.is_cliente === false ? ([conducente.cognome, conducente.nome].filter(Boolean).join(" ") || conducente.nominativo || "Non cliente") : "Il cliente"}</div></div>
                  <div className="rounded-md border border-slate-700 p-3"><span className="text-[11px] uppercase text-slate-500 font-mono">Luogo</span><div className="text-slate-100 mt-1">{[form.luogo_sinistro, form.luogo_provincia].filter(Boolean).join(" · ") || "—"}</div></div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-blue-700 border border-blue-500 px-6 py-6 flex items-center gap-4" data-testid="wizard-success-banner">
                <CheckCircle2 className="h-8 w-8 text-white shrink-0" />
                <div>
                  <div className="text-white font-bold text-base tracking-wide">PRATICA INSERITA CORRETTAMENTE CON IL NUMERO:</div>
                  <div className="text-amber-400 font-extrabold font-mono text-2xl mt-1" data-testid="wizard-numero">{createdNumero}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {missOpen && missingKeysForStep(step).length > 0 && (
          <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-4 mb-4" data-testid="wizard-missing-panel">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-red-400 shrink-0" />
              <span className="text-sm font-semibold text-red-300">Campi obbligatori mancanti in questo step</span>
              <button type="button" onClick={() => setMissOpen(false)} data-testid="wizard-missing-close" className="ml-auto text-slate-400 hover:text-slate-200"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-xs text-red-200/80 mb-2">Clicca su una voce per andare al campo. Compilandolo verrai portato automaticamente al successivo.</p>
            <ul className="space-y-1">
              {missingKeysForStep(step).map((k) => (
                <li key={k}>
                  <button type="button" onClick={() => focusField(k)} data-testid={`wizard-missing-item-${k}`}
                    className="flex items-center gap-2 text-sm text-red-200 hover:text-white underline underline-offset-2">
                    <ChevronRight className="h-3.5 w-3.5 shrink-0" /> {labelFor(k)}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div ref={footerRef} className="flex items-center justify-between border-t border-slate-800 -mx-6 -mb-6 mt-6 px-6 py-4">
          <Button type="button" onClick={chiudi} data-testid="wizard-annulla" className={btnDark}>Annulla</Button>
          <div className="flex items-center gap-3">
            {step < 5 && !createdNumero && footerMissing.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap justify-end text-xs" data-testid="wizard-footer-missing">
                <span className="text-[#c0392b] font-bold">Mancano:</span>
                {footerMissing.map((m, idx) => (
                  <span key={m.id} className="text-red-300">
                    <button type="button" onClick={() => { if (m.id === "veicoli_tipo") setVeicoloWalk(true); focusByTestId(m.tid); }} data-testid={`wizard-footer-missing-${m.id}`}
                      className="text-[#c0392b] hover:text-[#8f2a1f] underline underline-offset-2 font-semibold">{m.label}</button>{idx < footerMissing.length - 1 ? "," : ""}
                  </span>
                ))}
              </div>
            )}
            {step > 0 && !createdNumero && <Button type="button" onClick={() => setStep((s) => s - 1)} data-testid="wizard-indietro" className={btnDark}><ChevronLeft className="h-4 w-4 mr-1" /> Indietro</Button>}
            {step < 5 && <Button type="button" onClick={goNext} disabled={stepBlocked} data-testid="wizard-avanti" className={`${btnPrimary} disabled:opacity-50 disabled:cursor-not-allowed`}>Avanti <ChevronRight className="h-4 w-4 ml-1" /></Button>}
            {step === 5 && !createdNumero && <Button type="button" onClick={confermaGuard} disabled={saving} data-testid="wizard-conferma" className={btnPrimary}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Conferma e Salva"}</Button>}
            {step === 5 && createdNumero && (
              <>
                {onOpenCreated && createdData && (
                  <Button type="button" onClick={() => { onOpenCreated(createdData); onOpenChange(false); }} data-testid="wizard-apri-pratica" className={btnPrimary}>Apri pratica</Button>
                )}
                <Button type="button" onClick={chiudi} data-testid="wizard-chiudi" className={btnDark}>Chiudi</Button>
              </>
            )}
          </div>
        </div>
        {segnOpen && (
          <SegnalatoreSchedaDialog rec={segnRec}
            onSaved={(rec) => { set("segnalatore", displayName(rec)); setSegnOpen(false); setSegnRec(null); }}
            onCancel={() => { setSegnOpen(false); setSegnRec(null); }} />
        )}
      </DialogContent>
    </Dialog>
  );
}
